import { CHANNELS } from "../../shared/channels";
import {
  Actor,
  BLEND_MODES,
  BOARD_PAPERS,
  BOARD_PATTERNS,
  BOARD_TOOLS,
  Board,
  BoardLayer,
  BoardOp,
  BoardOpsEvent,
  BoardPattern,
  BoardPresence,
  BoardStroke,
  BrushTool,
  MAX_FRAMES,
  MAX_LAYERS,
  Matrix,
  RETOUCH_TOOLS,
  applyOps,
  canDrawOn,
  canEditLayer,
  canEditStroke,
  canRub,
  invertMatrix,
  isPixelTool,
  layerTimeline,
  liveLayers,
  newLayer,
  permitOps,
  userColor
} from "../../shared/boards";
import { Doodle } from "../../shared/types";
import { call, listen } from "../bridge";
import { t } from "../i18n";
import { byId, flashToast } from "../dom";
import { iconSvg } from "../icons";
import { amOwner, canUse, myId, myName } from "../me";
import { buzz } from "../tweens";
import { paint as paintBrushPanel, setupBrushPanel, showBrushes } from "./whiteboard_brushes";
import { Box, FONT_STACK, RetouchState, TOOL_FEEL, boxesMeet, continueRetouch, forgetStroke, freshRetouch, insideOutline, isAnimated, joinBoxes, paintStroke, smoothPoint, strokeBox, strokeHit, textBox } from "./whiteboard_ink";
import { Kit, SHAPES, SWATCHES, ShapeKind, isLineBrush, loadKit, saveKit } from "./whiteboard_kit";
import { View, MAX_SCALE, MIN_SCALE, between, boardExtent, cssOf, degrees, flipAround, freshView, multiply, onStage, placeView, radians, readView, sameView, screenExtent, snapped, toBoardPoint, toScreenPoint, turnBy, turnTo, viewMatrix, wrap, zoomTo } from "./whiteboard_camera";
import { clearPresence, movePresence, setupPresence, showPresence } from "./whiteboard_presence";

// one open whiteboard: a stack of canvases, one per layer, over the paper. strokes are drawn onto a live canvas
// while the pen moves, folded into their layer when it lifts and sent to the server. everyone else's strokes
// come back through the live events and land on the same layers. a layer can hold animation frames: whoever is
// working in the layer sees the frame they are drawing, everyone else sees it play

type Mode = BrushTool | "hand" | "turn" | "picker" | "strokeEraser" | "select" | "lasso" | "lassoFill" | "shape" | "text";

interface Live {
  pointer: number;
  fromTouch: boolean;
  startedAt: number;
  stroke: BoardStroke;
  last: [number, number] | null;
  lastAt: number;
  lastScreen: [number, number];
  pressure: number;
  // the layer's own canvas
  canvas: HTMLCanvasElement;
  // the eraser rubs straight out of the layer, everything else is drawn on the live canvas above it
  direct: boolean;
  drawn: number;
  smooth: number;
  shape: ShapeKind | null;
  origin: [number, number];
  // where a retouch stroke has got to, so it is painted bit by bit as the pen moves
  retouch: RetouchState | null;
}

interface Gesture {
  // board point under the fingers' middle, and the finger spread, when it started
  anchor: [number, number];
  spread: number;
  scale: number;
  // the twist of the fingers and the view's turn when it started, and whether the twist has gone far enough to count
  twist: number;
  rot: number;
  turning: boolean;
}

interface Step {
  undo: BoardOp[];
  redo: BoardOp[];
}

type Handle = "nw" | "ne" | "sw" | "se" | "rot" | "inside" | "";

interface Transform {
  pointer: number;
  handle: Handle;
  start: [number, number];
  center: [number, number];
  anchor: [number, number];
  box: Box;
  m: Matrix;
  moved: boolean;
}

const VIEW_KEY = "chix-wb-view-";
const PATTERN_LABEL: Record<BoardPattern, string> = { none: "plain", dots: "dots", grid: "grid", lines: "lines" };
const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];
const PAPER_STEP = 32;

let board: Board | null = null;
let rev = 0;
let view: View = freshView();
let painted: View = freshView();
let paintMs = 0;
let dpr = 1;
let width = 0;
let height = 0;
let activeLayer = "";
let mode: Mode = "pen";
let lastEraser: "eraser" | "strokeEraser" = "eraser";
let kit: Kit = loadKit();
let live: Live | null = null;
let liveFrame = 0;
let liveCanvas: HTMLCanvasElement | null = null;
let scratch: HTMLCanvasElement | null = null;
let staleLayers = new Set<string>();
let gesture: Gesture | null = null;
const fingers = new Map<number, { x: number; y: number; sx: number; sy: number; type: string }>();
let tap: { fingers: number; at: number; moved: boolean } | null = null;
// the turn tool: where the pointer was and how the view was turned when it went down
let spinning: { pointer: number; from: number; rot: number } | null = null;
// the lasso: the outline drawn so far in screen points, for picking or for filling
let lassoing: { pointer: number; pts: number[]; fill: boolean } | null = null;
let spaceDown = false;
let undoStack: Step[] = [];
let redoStack: Step[] = [];
let outbox: Array<{ board: string; op: BoardOp }> = [];
let sending = false;
let thumbTimer = 0;
let thumbDirty = false;
let repaintTimer = 0;
let lastStructure = "";
let wheelTimer = 0;
const canvases = new Map<string, HTMLCanvasElement>();
const boxes = new WeakMap<BoardStroke, Box>();
let onClose: () => void = () => {};
let people: Record<string, string> = {};
const session = Array.from(crypto.getRandomValues(new Uint8Array(6)), (byte) => byte.toString(16).padStart(2, "0")).join("");

// animation frames: the frame each layer is being drawn on here, whether the layer I'm in is playing, and the
// layers that need repainting as the clock moves
const editFrames = new Map<string, string>();
let previewing = false;
let animRaf = 0;
let animLast = 0;
let animInfo = new Map<string, { boil: boolean; plays: boolean }>();
const shownFrames = new Map<string, string>();

// strokes on each layer, rebuilt when strokes change, so painting a layer never scans the whole board
let index: Map<string, BoardStroke[]> | null = null;

// strokes picked up, or about to be erased, so their layer leaves them out while they are floating
const hiddenIds = new Set<string>();
let selected: string[] = [];
let transform: Transform | null = null;
let marquee: { pointer: number; x0: number; y0: number; x1: number; y1: number } | null = null;
let rubbing: { pointer: number; ids: Set<string>; layers: Set<string> } | null = null;
let typing: { x: number; y: number; layer: string; frame: string; replace: BoardStroke | null } | null = null;
let presenceAt = 0;

// --- saving what this person carries

function keepKit(): void {
  saveKit(kit);
}

function saveView(): void {
  if (!board) return;
  try {
    localStorage.setItem(VIEW_KEY + board.id, JSON.stringify(view));
  } catch {
    // fine, the board opens fitted next time
  }
}

function savedView(id: string): View | null {
  try {
    return readView(JSON.parse(localStorage.getItem(VIEW_KEY + id) || "null"));
  } catch {
    // ignore
  }
  return null;
}

// --- who may do what

function actor(): Actor {
  return { id: myId(), admin: amOwner() };
}

function canDraw(): boolean {
  return Boolean(board) && (!board!.shared || canUse("whiteboard", "edit"));
}

function canStyleBoard(): boolean {
  return Boolean(board) && (!board!.shared || amOwner());
}

function layer(id = activeLayer): BoardLayer | undefined {
  return board?.layers.find((item) => item.id === id && !item.goneAt);
}

function mayDrawOn(item: BoardLayer | undefined): boolean {
  return Boolean(board) && canDraw() && canDrawOn(board!, item, actor());
}

function mayEditLayer(item: BoardLayer | undefined): boolean {
  return Boolean(board) && canDraw() && canEditLayer(board!, item, actor());
}

function whose(item: BoardLayer): string {
  if (item.owner && item.owner === myId()) return t("you");
  return people[item.owner] || t("someone");
}

function newId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

// --- coordinates

function toBoard(x: number, y: number, at: View = view): [number, number] {
  return toBoardPoint(at, x, y);
}

function toScreen(x: number, y: number): [number, number] {
  return toScreenPoint(view, x, y);
}

// the board area on screen, as the box that holds it when the view is turned
function screenBox(): Box {
  return boardExtent(view, width, height);
}

// draw in board units, the way this view sees the board
function applyView(ctx: CanvasRenderingContext2D, at: View = view): void {
  const m = viewMatrix(at);
  ctx.setTransform(dpr * m[0], dpr * m[1], dpr * m[2], dpr * m[3], dpr * m[4], dpr * m[5]);
}

function boxOf(stroke: BoardStroke): Box {
  let box = boxes.get(stroke);
  if (!box) {
    box = strokeBox(stroke);
    boxes.set(stroke, box);
  }
  return box;
}

function stagePoint(event: { clientX: number; clientY: number }): [number, number] {
  const rect = byId("wb-stage").getBoundingClientRect();
  return [event.clientX - rect.left, event.clientY - rect.top];
}

// --- strokes and frames

function strokesChanged(): void {
  index = null;
}

function strokesOf(layerId: string): BoardStroke[] {
  if (!board) return [];
  if (!index) {
    index = new Map();
    for (const stroke of board.strokes) {
      if (stroke.goneAt) continue;
      const list = index.get(stroke.layer);
      if (list) list.push(stroke);
      else index.set(stroke.layer, [stroke]);
    }
  }
  return index.get(layerId) || [];
}

function strokesOnFrame(layerId: string, frame: string): BoardStroke[] {
  return strokesOf(layerId).filter((stroke) => (stroke.frame || "") === frame);
}

function currentFrame(item: BoardLayer): string {
  const timeline = layerTimeline(item);
  const saved = editFrames.get(item.id);
  return saved !== undefined && timeline.includes(saved) ? saved : timeline[0];
}

// in a layer means it is the one being drawn on, and nobody who can't draw is ever in a layer
function inLayer(item: BoardLayer): boolean {
  return item.id === activeLayer && canDraw() && !previewing;
}

function shownFrame(item: BoardLayer, time: number): string {
  const timeline = layerTimeline(item);
  if (timeline.length < 2) return timeline[0];
  if (inLayer(item)) return currentFrame(item);
  return timeline[Math.floor((time / 1000) * item.fps) % timeline.length];
}

function frameTag(item: BoardLayer): string | undefined {
  return layerTimeline(item).length > 1 || item.frames.length ? currentFrame(item) || undefined : undefined;
}

// --- painting

function sizeCanvas(canvas: HTMLCanvasElement): void {
  const w = Math.max(1, Math.round(width * dpr));
  const h = Math.max(1, Math.round(height * dpr));
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
}

function structureKey(): string {
  return board ? liveLayers(board).map((item) => `${item.id}:${item.hidden}:${item.opacity}:${item.mode}`).join() : "";
}

// one canvas per layer in board order, hidden layers give theirs back so a dozen layers fit in an ipad's memory
function syncCanvases(): void {
  if (!board) return;
  const host = byId("wb-canvases");
  const layers = liveLayers(board);
  const keep = new Set(layers.map((item) => item.id));
  for (const [id, canvas] of canvases) {
    if (keep.has(id)) continue;
    canvas.remove();
    canvases.delete(id);
  }
  layers.forEach((item) => {
    let canvas = canvases.get(item.id);
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvas.className = "wb-layer";
      canvases.set(item.id, canvas);
    }
    canvas.style.opacity = String(item.opacity);
    canvas.style.mixBlendMode = item.mode === "normal" ? "" : item.mode;
    canvas.hidden = item.hidden;
    if (item.hidden) {
      canvas.width = 1;
      canvas.height = 1;
    }
    host.append(canvas);
  });
  if (live && !live.direct && liveCanvas) canvases.get(live.stroke.layer)?.after(liveCanvas);
}

function onionFor(item: BoardLayer, frame: string): Array<{ frame: string; tint: string; alpha: number }> {
  const { on, before, after } = kit.onion;
  const timeline = layerTimeline(item);
  const at = timeline.indexOf(frame);
  if (!on || timeline.length < 2 || at < 0 || previewing || !inLayer(item)) return [];
  const skins: Array<{ frame: string; tint: string; alpha: number }> = [];
  for (let step = Math.min(before, at); step >= 1; step--) skins.push({ frame: timeline[at - step], tint: "#ff4d6d", alpha: 0.36 / step });
  for (let step = Math.min(after, timeline.length - 1 - at); step >= 1; step--) skins.push({ frame: timeline[at + step], tint: "#2ec486", alpha: 0.32 / step });
  return skins;
}

// the frames next to the one being drawn, ghosted in red (before) and green (after) beneath it
function paintOnion(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, item: BoardLayer, frame: string, seen: Box): void {
  const skins = onionFor(item, frame);
  if (!skins.length) return;
  if (!scratch) scratch = document.createElement("canvas");
  if (scratch.width !== canvas.width || scratch.height !== canvas.height) {
    scratch.width = canvas.width;
    scratch.height = canvas.height;
  }
  const paint = scratch.getContext("2d");
  if (!paint) return;
  for (const skin of skins) {
    paint.setTransform(1, 0, 0, 1, 0, 0);
    paint.globalCompositeOperation = "source-over";
    paint.clearRect(0, 0, scratch.width, scratch.height);
    applyView(paint);
    for (const stroke of strokesOnFrame(item.id, skin.frame)) {
      if (boxesMeet(boxOf(stroke), seen)) paintStroke(paint, stroke, 0);
    }
    paint.setTransform(1, 0, 0, 1, 0, 0);
    paint.globalCompositeOperation = "source-in";
    paint.fillStyle = skin.tint;
    paint.fillRect(0, 0, scratch.width, scratch.height);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = skin.alpha;
    ctx.drawImage(scratch, 0, 0);
    ctx.restore();
  }
}

function paintLayer(id: string, time = performance.now()): void {
  const item = layer(id);
  const canvas = canvases.get(id);
  if (!item || !canvas || item.hidden) return;
  if (live?.direct && live.stroke.layer === id) {
    staleLayers.add(id);
    return;
  }
  sizeCanvas(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const frame = shownFrame(item, time);
  shownFrames.set(id, frame);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  applyView(ctx);
  const seen = screenBox();
  paintOnion(ctx, canvas, item, frame, seen);
  for (const stroke of strokesOnFrame(id, frame)) {
    if (!hiddenIds.has(stroke.id) && boxesMeet(boxOf(stroke), seen)) paintStroke(ctx, stroke, time);
  }
}

function paintPaper(): void {
  if (!board) return;
  const stage = byId("wb-stage");
  stage.style.backgroundColor = board.paper;
  const dark = isDark(board.paper);
  stage.classList.toggle("dark-paper", dark);
  const canvas = byId<HTMLCanvasElement>("wb-paper");
  sizeCanvas(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  // too small to tell apart, so the paper stays plain
  if (board.pattern === "none" || PAPER_STEP * view.scale < 6) return;
  const ink = dark ? "rgba(255,255,255,0.12)" : "rgba(30,40,60,0.14)";
  const seen = screenBox();
  const from = (value: number) => Math.floor(value / PAPER_STEP) * PAPER_STEP;
  applyView(ctx);
  if (board.pattern === "dots") {
    // one dot drawn at twice the size, tiled over the visible board and turned with the view
    const radius = (1.4 * Math.min(view.scale, 1.5)) / view.scale;
    const tile = document.createElement("canvas");
    tile.width = PAPER_STEP * 2;
    tile.height = PAPER_STEP * 2;
    const dab = tile.getContext("2d");
    if (!dab) return;
    dab.fillStyle = ink;
    dab.beginPath();
    dab.arc(PAPER_STEP, PAPER_STEP, radius * 2, 0, Math.PI * 2);
    dab.fill();
    // the pattern takes a copy of the tile, so it is made once the dot is drawn
    const pattern = ctx.createPattern(tile, "repeat");
    if (!pattern) return;
    pattern.setTransform(new DOMMatrix([0.5, 0, 0, 0.5, -PAPER_STEP / 2, -PAPER_STEP / 2]));
    ctx.fillStyle = pattern;
    ctx.fillRect(seen.x0 - PAPER_STEP, seen.y0 - PAPER_STEP, seen.x1 - seen.x0 + PAPER_STEP * 2, seen.y1 - seen.y0 + PAPER_STEP * 2);
    return;
  }
  const lines = new Path2D();
  for (let y = from(seen.y0); y <= seen.y1; y += PAPER_STEP) {
    lines.moveTo(seen.x0, y);
    lines.lineTo(seen.x1, y);
  }
  if (board.pattern === "grid") {
    for (let x = from(seen.x0); x <= seen.x1; x += PAPER_STEP) {
      lines.moveTo(x, seen.y0);
      lines.lineTo(x, seen.y1);
    }
  }
  ctx.strokeStyle = ink;
  ctx.lineWidth = 1 / view.scale;
  ctx.stroke(lines);
}

function isDark(hex: string): boolean {
  const value = parseInt(hex.slice(1), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b < 110;
}

function paintAll(): void {
  if (!board) return;
  const started = performance.now();
  painted = { ...view };
  byId("wb-canvases").style.transform = "";
  liveLayers(board).forEach((item) => paintLayer(item.id));
  paintPaper();
  paintSelection();
  movePresence();
  paintMs = performance.now() - started;
}

// while two fingers move the board, small boards repaint every frame and big ones slide their last picture
// along until the fingers stop
function viewChanged(): void {
  paintPaper();
  movePresence();
  paintViewUi();
  if (paintMs < 10) {
    paintAll();
    return;
  }
  byId("wb-canvases").style.transform = cssOf(between(painted, view));
  paintSelection();
  window.clearTimeout(repaintTimer);
  repaintTimer = window.setTimeout(() => {
    if (!gesture) paintAll();
  }, 140);
}

function resize(): void {
  const stage = byId("wb-stage");
  const rect = stage.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  width = rect.width;
  height = rect.height;
  dpr = Math.min(window.devicePixelRatio || 1, 2);
  paintAll();
}

function contentBox(): Box | null {
  if (!board) return null;
  const shown = new Set(liveLayers(board).filter((item) => !item.hidden).map((item) => item.id));
  let box: Box | null = null;
  for (const stroke of board.strokes) {
    if (stroke.goneAt || !shown.has(stroke.layer) || isPixelTool(stroke.tool)) continue;
    box = joinBoxes(box, boxOf(stroke));
  }
  return box;
}

// the whole drawing on screen, in whatever way the view is turned now
function fit(): void {
  const box = contentBox();
  const look = { rot: view.rot, flip: view.flip };
  if (!box) {
    view = placeView({ scale: 1, ...look }, [width / 2, height / 2], [0, 0]);
  } else {
    const pad = 48;
    const reach = screenExtent({ x: 0, y: 0, scale: 1, ...look }, box);
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.min((width - pad * 2) / (reach.x1 - reach.x0 || 1), (height - pad * 2) / (reach.y1 - reach.y0 || 1), 2)));
    view = placeView({ scale, ...look }, [width / 2, height / 2], [(box.x0 + box.x1) / 2, (box.y0 + box.y1) / 2]);
  }
  settleView();
}

function stageCentre(): [number, number] {
  return [width / 2, height / 2];
}

// a view change from a key or a button: it lands at once and is remembered
function settleView(): void {
  if (!board) return;
  if (typing) commitText();
  window.clearTimeout(repaintTimer);
  paintAll();
  paintViewUi();
  saveView();
}

function turnView(by: number, around: [number, number] = stageCentre()): void {
  view = turnBy(view, by, around);
  settleView();
  say(`view turned to ${degrees(view.rot)} degrees`);
}

function turnViewTo(rot: number): void {
  view = turnTo(view, rot, stageCentre());
  settleView();
}

function flipView(): void {
  view = flipAround(view, stageCentre());
  settleView();
  say(view.flip ? "view mirrored" : "view back the right way round");
}

// back upright and the right way round, around the middle of the screen
function uprightView(): void {
  let next = turnTo(view, 0, stageCentre());
  if (next.flip) next = flipAround(next, stageCentre());
  view = next;
  settleView();
  say("view upright");
}

function zoomView(scale: number): void {
  view = zoomTo(view, scale, stageCentre());
  settleView();
}

function zoomAt(x: number, y: number, scale: number): void {
  view = zoomTo(view, scale, [x, y]);
  viewChanged();
}

// tells screen readers what a key did to the view
function say(text: string): void {
  byId("wb-live-say").textContent = text;
}

// --- animation: layers with frames play, brushes that move keep moving

function scanAnim(): void {
  animInfo = new Map();
  if (!board) return;
  for (const item of liveLayers(board)) {
    if (item.hidden) continue;
    const plays = layerTimeline(item).length > 1 && !inLayer(item);
    const boil = strokesOf(item.id).some(isAnimated);
    if (plays || boil) animInfo.set(item.id, { boil, plays });
  }
  if (animInfo.size && !animRaf) animRaf = requestAnimationFrame(animTick);
}

function animTick(now: number): void {
  animRaf = 0;
  if (!board || !animInfo.size) return;
  animRaf = requestAnimationFrame(animTick);
  // thirty a second is plenty for flipping frames and shivering lines, and leaves the pen the rest
  if (now - animLast < 32 || gesture || transform) return;
  animLast = now;
  for (const [id, info] of animInfo) {
    const item = layer(id);
    if (!item) continue;
    if (info.boil || shownFrame(item, now) !== shownFrames.get(id)) paintLayer(id, now);
  }
}

// --- sending changes

function setSync(state: "saved" | "saving" | "offline"): void {
  const dot = byId("wb-sync");
  dot.dataset.state = state;
  dot.title = state === "saved" ? t("saved") : state === "saving" ? t("saving") : t("offline, will send when back");
}

function send(ops: BoardOp[]): void {
  if (!board || !ops.length) return;
  const id = board.id;
  outbox.push(...ops.map((op) => ({ board: id, op })));
  thumbDirty = true;
  window.clearTimeout(thumbTimer);
  thumbTimer = window.setTimeout(() => void sendThumb(), 5000);
  void flush();
}

async function flush(): Promise<void> {
  if (sending || !outbox.length) return;
  sending = true;
  setSync("saving");
  const target = outbox[0].board;
  let count = 0;
  while (count < outbox.length && count < 200 && outbox[count].board === target) count++;
  const batch = outbox.splice(0, count);
  try {
    await call(CHANNELS.boardsApply, target, batch.map((item) => item.op));
    sending = false;
  } catch (error) {
    sending = false;
    // no network: keep the strokes and try again. anything else the server said no to
    if (error instanceof TypeError) {
      outbox.unshift(...batch);
      setSync("offline");
      window.setTimeout(() => void flush(), 2500);
      return;
    }
    flashToast(error instanceof Error ? error.message : t("could not save that"));
    if (board?.id === target) void resync();
  }
  if (outbox.length) void flush();
  else setSync("saved");
}

// a small picture for the shelf, sent a few seconds after drawing stops
async function sendThumb(): Promise<void> {
  if (!board || !thumbDirty || !canDraw()) return;
  thumbDirty = false;
  const picture = render(320, 240);
  if (!picture) return;
  try {
    await call(CHANNELS.boardsThumb, board.id, picture.toDataURL("image/jpeg", 0.78));
  } catch {
    // the shelf just keeps the old picture
  }
}

function record(step: Step): void {
  undoStack.push(step);
  if (undoStack.length > 200) undoStack.shift();
  redoStack = [];
  paintHistory();
}

// ops that are mine to make, applied here straight away and sent on
function run(ops: BoardOp[]): void {
  if (!board) return;
  const allowed = permitOps(board, ops, actor());
  if (allowed.length < ops.length) flashToast(t("that's someone else's, only they can change it"));
  if (!allowed.length) return;
  const touched = applyOps(board, allowed);
  strokesChanged();
  afterChange(touched);
  send(allowed);
}

function undo(): void {
  const step = undoStack.pop();
  if (!step || !canDraw()) return;
  run(step.undo);
  redoStack.push(step);
  paintHistory();
  buzz();
}

function redo(): void {
  const step = redoStack.pop();
  if (!step || !canDraw()) return;
  run(step.redo);
  undoStack.push(step);
  paintHistory();
  buzz();
}

function paintHistory(): void {
  byId<HTMLButtonElement>("wb-undo").disabled = !undoStack.length;
  byId<HTMLButtonElement>("wb-redo").disabled = !redoStack.length;
}

// what changed decides what repaints: layer settings need the stack rebuilt, strokes only their own layer
function afterChange(touched: Set<string>): void {
  if (!board) return;
  if (touched.has("*")) {
    paintTitle();
    paintPaper();
  }
  const structure = structureKey();
  if (structure !== lastStructure) {
    lastStructure = structure;
    syncCanvases();
    liveLayers(board).forEach((item) => paintLayer(item.id));
  } else {
    touched.forEach((id) => id !== "*" && paintLayer(id));
  }
  if (!layer()) activeLayer = defaultLayer();
  // anything picked up that is gone now stays out of the selection
  if (selected.length) {
    const here = new Set(board.strokes.filter((stroke) => !stroke.goneAt).map((stroke) => stroke.id));
    selected = selected.filter((id) => here.has(id));
  }
  refreshUi();
}

function refreshUi(): void {
  paintLayers();
  paintTimeline();
  paintOptions();
  paintSelection();
  scanAnim();
}

// the top layer this person can draw on, or just the top one
function defaultLayer(): string {
  if (!board) return "";
  const layers = liveLayers(board).slice().reverse();
  return (layers.find((item) => mayDrawOn(item)) || layers[0])?.id || "";
}

// --- live events from everyone else

async function resync(): Promise<void> {
  if (!board) return;
  const id = board.id;
  try {
    const fresh = await call<Board>(CHANNELS.boardsGet, id);
    if (board?.id !== id) return;
    // ops still waiting to go out stay drawn
    const waiting = outbox.filter((item) => item.board === id).map((item) => item.op);
    board = fresh;
    people = fresh.people || people;
    rev = fresh.rev;
    applyOps(board, waiting);
    strokesChanged();
    lastStructure = "";
    afterChange(new Set(["*"]));
    paintAll();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("lost the board"));
  }
}

function onOps(event: BoardOpsEvent): void {
  if (!board || event.board !== board.id || event.rev <= rev) return;
  if (event.name) people[event.by] = event.name;
  if (event.from !== rev) {
    void resync();
    return;
  }
  // my own strokes coming back in the order i drew them change nothing on screen
  const tail = board.strokes.slice(-event.ops.length).map((stroke) => stroke.id).join();
  const echo = event.by === myId() && event.ops.every((op) => op.type === "stroke")
    && event.ops.map((op) => (op as { stroke: BoardStroke }).stroke.id).join() === tail;
  if (echo) {
    rev = event.rev;
    return;
  }
  const touched = applyOps(board, event.ops);
  strokesChanged();
  rev = event.rev;
  afterChange(touched);
}

function onPresence(event: BoardPresence & { shared?: boolean }): void {
  if (!board || event.board !== board.id || event.session === session) return;
  showPresence(event);
}

// tells everyone else where my pen is. at most about fifteen a second, and only on a board others can be on
function ping(event: { clientX: number; clientY: number } | null, down: boolean, gone = false, force = false): void {
  if (!board?.shared) return;
  const now = performance.now();
  if (!gone && !force && now - presenceAt < 66) return;
  presenceAt = now;
  const at = event ? toBoard(...stagePoint(event)) : [0, 0];
  const key = faderKey();
  void call(CHANNELS.boardsPresence, board.id, {
    session,
    x: at[0],
    y: at[1],
    down,
    color: kit.color,
    size: (kit.sizes[key] || 8) / view.scale,
    ...(gone ? { gone: true } : {})
  }).catch(() => {});
}

// --- drawing

function pressureOf(event: PointerEvent, previous: number, speed: number): number {
  if (event.pointerType === "pen") return Math.min(1, Math.max(0.02, event.pressure || 0.02));
  // fingers and mice have no pressure, so fast lines get thinner like a quick flick of a brush
  const target = Math.min(1, Math.max(0.3, 1 - speed / 3.2));
  return previous * 0.75 + target * 0.25;
}

function ensureLive(): HTMLCanvasElement {
  if (!liveCanvas) {
    liveCanvas = document.createElement("canvas");
    liveCanvas.className = "wb-layer wb-live";
    liveCanvas.hidden = true;
  }
  return liveCanvas;
}

// the points of a shape from its two corners, packed with plenty of points so wobbly brushes have something to wobble
function shapePoints(kind: ShapeKind, x0: number, y0: number, x1: number, y1: number, square: boolean, gap: number): number[] {
  let dx = x1 - x0;
  let dy = y1 - y0;
  if (square && kind !== "line" && kind !== "arrow") {
    const side = Math.max(Math.abs(dx), Math.abs(dy));
    dx = Math.sign(dx || 1) * side;
    dy = Math.sign(dy || 1) * side;
  } else if (square) {
    // lines snap to every 15 degrees
    const length = Math.hypot(dx, dy);
    const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 12)) * (Math.PI / 12);
    dx = Math.cos(angle) * length;
    dy = Math.sin(angle) * length;
  }
  const ex = x0 + dx;
  const ey = y0 + dy;
  const path: Array<[number, number]> = [];
  if (kind === "line") path.push([x0, y0], [ex, ey]);
  if (kind === "arrow") {
    const angle = Math.atan2(dy, dx);
    const head = Math.min(Math.hypot(dx, dy) * 0.4, 28 / view.scale + 10);
    path.push([x0, y0], [ex, ey], [ex - Math.cos(angle - 0.45) * head, ey - Math.sin(angle - 0.45) * head], [ex, ey], [ex - Math.cos(angle + 0.45) * head, ey - Math.sin(angle + 0.45) * head]);
  }
  if (kind === "box") path.push([x0, y0], [ex, y0], [ex, ey], [x0, ey], [x0, y0]);
  if (kind === "ellipse") {
    const steps = 72;
    for (let at = 0; at <= steps; at++) {
      const turn = (at / steps) * Math.PI * 2;
      path.push([x0 + dx / 2 + (Math.cos(turn) * dx) / 2, y0 + dy / 2 + (Math.sin(turn) * dy) / 2]);
    }
  }
  const out: number[] = [];
  for (let at = 0; at < path.length; at++) {
    const [px, py] = path[at];
    if (at) {
      const [qx, qy] = path[at - 1];
      const pieces = Math.min(60, Math.floor(Math.hypot(px - qx, py - qy) / gap));
      for (let piece = 1; piece < pieces; piece++) out.push(qx + ((px - qx) * piece) / pieces, qy + ((py - qy) * piece) / pieces, 0.7);
    }
    out.push(px, py, 0.7);
  }
  return out;
}

function beginStroke(event: PointerEvent, x: number, y: number): void {
  if (!board) return;
  const target = layer();
  if (!target) return;
  if (target.hidden) return flashToast(t("that layer is hidden, show it to draw"));
  if (target.locked) return flashToast(t("that layer is locked"));
  if (!mayDrawOn(target)) return flashToast(t("that layer is {who}'s, ask them to let you draw on it", { who: whose(target) }));
  const tool: BrushTool = mode === "shape" || !BOARD_TOOLS.includes(mode as BrushTool) ? kit.tool : mode as BrushTool;
  if (isPixelTool(tool) && !canRub(board, target.id, actor())) return flashToast(t(tool === "eraser" ? "that layer has other people's lines, use the stroke eraser" : "that layer has other people's lines, retouch on a layer of your own"));
  const canvas = canvases.get(target.id);
  if (!canvas) return;
  if (previewing) setPreview(false);
  const settings = kit.settings[tool];
  const [bx, by] = toBoard(x, y);
  const pressure = event.pointerType === "pen" ? Math.max(0.02, event.pressure || 0.02) : 0.6;
  const shape = mode === "shape" ? kit.shape : null;
  const direct = isPixelTool(tool);
  const frame = frameTag(target);
  live = {
    pointer: event.pointerId,
    fromTouch: event.pointerType === "touch",
    startedAt: performance.now(),
    stroke: {
      id: newId(),
      layer: target.id,
      tool,
      color: kit.color,
      size: kit.sizes[tool] / view.scale,
      opacity: kit.alphas[tool],
      soft: settings.soft,
      press: shape ? 0 : settings.press,
      gap: settings.gap,
      ...(frame ? { frame } : {}),
      pts: [bx, by, shape ? 0.7 : pressure],
      by: myId(),
      at: Date.now()
    },
    last: [bx, by],
    lastAt: event.timeStamp,
    lastScreen: [x, y],
    pressure,
    canvas,
    direct,
    drawn: 0,
    smooth: shape ? 0 : settings.smooth,
    shape,
    origin: [bx, by],
    retouch: isRetouchMode(tool) ? freshRetouch() : null
  };
  if (direct) {
    rub(true);
  } else {
    const overlay = ensureLive();
    sizeCanvas(overlay);
    overlay.hidden = false;
    // the stroke's own opacity rides on the canvas, so one see-through stroke never darkens where it crosses itself
    overlay.style.opacity = String(target.opacity * live.stroke.opacity);
    overlay.style.mixBlendMode = target.mode === "normal" ? "" : target.mode;
    canvas.after(overlay);
  }
  queueLive();
}

// the eraser takes bites out of the layer as it goes, only the newest piece each time
function rub(first = false): void {
  if (!live?.direct) return;
  const ctx = live.canvas.getContext("2d");
  if (!ctx) return;
  if (live.retouch) {
    applyView(ctx);
    continueRetouch(ctx, live.stroke, live.retouch);
    return;
  }
  const points = live.stroke.pts;
  const from = first ? 0 : Math.max(0, live.drawn - 1);
  const piece = { ...live.stroke, pts: points.slice(from * 3) };
  applyView(ctx);
  paintStroke(ctx, piece, 0);
  live.drawn = points.length / 3;
}

function extendStroke(event: PointerEvent): void {
  if (!live) return;
  const samples = typeof event.getCoalescedEvents === "function" ? event.getCoalescedEvents() : [];
  for (const sample of samples.length ? samples : [event]) {
    const [x, y] = stagePoint(sample);
    const moved = Math.hypot(x - live.lastScreen[0], y - live.lastScreen[1]);
    if (moved < 0.8) continue;
    const speed = moved / Math.max(1, sample.timeStamp - live.lastAt);
    live.pressure = pressureOf(sample, live.pressure, speed);
    live.lastScreen = [x, y];
    live.lastAt = sample.timeStamp;
    const [bx, by] = toBoard(x, y);
    if (live.shape) {
      live.stroke.pts = shapePoints(live.shape, live.origin[0], live.origin[1], bx, by, event.shiftKey, Math.max(3, live.stroke.size * 0.6));
      continue;
    }
    live.last = smoothPoint(live.smooth, live.last, bx, by);
    if (live.stroke.pts.length / 3 >= 3990) {
      endStroke();
      return;
    }
    live.stroke.pts.push(live.last[0], live.last[1], live.pressure);
  }
  if (live.direct) rub();
  queueLive();
}

function queueLive(): void {
  if (liveFrame) return;
  liveFrame = requestAnimationFrame(() => {
    liveFrame = 0;
    paintLive();
  });
}

function paintLive(): void {
  if (!live || live.direct || !liveCanvas) return;
  const ctx = liveCanvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, liveCanvas.width, liveCanvas.height);
  applyView(ctx);
  forgetStroke(live.stroke);
  paintStroke(ctx, live.stroke, performance.now(), 1);
  // a brush that moves keeps moving under the pen even when the pen is still
  if (isAnimated(live.stroke)) queueLive();
}

function clearLive(): void {
  if (!liveCanvas) return;
  liveCanvas.hidden = true;
  liveCanvas.getContext("2d")?.clearRect(0, 0, liveCanvas.width, liveCanvas.height);
}

function endStroke(): void {
  if (!live || !board) return;
  const done = live;
  live = null;
  if (liveFrame) cancelAnimationFrame(liveFrame);
  liveFrame = 0;
  const pts = done.stroke.pts;
  if (!done.shape) {
    // the last point lands exactly where the pen lifted, smoothing never leaves the line short
    const [lx, ly] = toBoard(done.lastScreen[0], done.lastScreen[1]);
    if (pts.length >= 6 && (pts[pts.length - 3] !== lx || pts[pts.length - 2] !== ly)) pts.push(lx, ly, done.pressure);
  } else if (pts.length < 6) {
    // a shape that was only tapped is nothing
    clearLive();
    if (done.direct) paintLayer(done.stroke.layer);
    return;
  }
  forgetStroke(done.stroke);
  board.strokes.push(done.stroke);
  strokesChanged();
  const item = layer(done.stroke.layer);
  if (done.direct || staleLayers.has(done.stroke.layer) || (item && onionFor(item, currentFrame(item)).length)) {
    staleLayers.delete(done.stroke.layer);
    clearLive();
    paintLayer(done.stroke.layer);
  } else {
    const ctx = done.canvas.getContext("2d");
    if (ctx) {
      applyView(ctx);
      paintStroke(ctx, done.stroke, performance.now());
    }
    clearLive();
  }
  send([{ type: "stroke", stroke: done.stroke }]);
  record({ undo: [{ type: "erase", ids: [done.stroke.id] }], redo: [{ type: "restore", ids: [done.stroke.id] }] });
  scanAnim();
  if (!byId("wb-layer-panel").hidden) paintLayers();
}

// a second finger right after the first means pinch, not a line
function dropStroke(): void {
  if (!live) return;
  const done = live;
  live = null;
  if (liveFrame) cancelAnimationFrame(liveFrame);
  liveFrame = 0;
  clearLive();
  // the eraser already bit into the layer, so it is drawn again from the strokes
  if (done.direct) paintLayer(done.stroke.layer);
  if (staleLayers.delete(done.stroke.layer)) paintLayer(done.stroke.layer);
}

function pickColor(x: number, y: number): void {
  if (!board) return;
  const layers = liveLayers(board).filter((item) => !item.hidden).reverse();
  for (const item of layers) {
    const ctx = canvases.get(item.id)?.getContext("2d");
    if (!ctx) continue;
    const [r, g, b, a] = ctx.getImageData(Math.floor(x * dpr), Math.floor(y * dpr), 1, 1).data;
    if (a < 24) continue;
    setColor(`#${[r, g, b].map((value) => value.toString(16).padStart(2, "0")).join("")}`);
    setMode(kit.tool);
    return;
  }
  setColor(board.paper);
  setMode(kit.tool);
}

// --- text

function beginText(x: number, y: number): void {
  if (!board) return;
  const target = layer();
  if (!target) return;
  if (target.hidden || target.locked) return flashToast(target.hidden ? t("that layer is hidden, show it to write") : t("that layer is locked"));
  if (!mayDrawOn(target)) return flashToast(t("that layer is {who}'s, ask them to let you draw on it", { who: whose(target) }));
  const [bx, by] = toBoard(x, y);
  const frame = shownFrame(target, performance.now());
  // a tap on one of my own bits of text opens it for editing
  const hit = strokesOnFrame(target.id, frame).reverse().find((stroke) => stroke.tool === "text" && strokeHit(stroke, bx, by, 4 / view.scale) && canEditStroke(board!, stroke, actor()));
  typing = { x: hit ? hit.pts[0] : bx, y: hit ? hit.pts[1] : by, layer: target.id, frame, replace: hit || null };
  const input = byId<HTMLTextAreaElement>("wb-text");
  const size = hit ? hit.size * view.scale : kit.sizes.text;
  input.value = hit?.text || "";
  input.style.font = `${size}px ${FONT_STACK[hit?.font || kit.font]}`;
  input.style.lineHeight = "1.25";
  input.style.color = hit?.color || kit.color;
  const [sx, sy] = toScreen(typing.x, typing.y);
  // the stage may sit away from the editor's corner now that tools dock around it
  const room = byId("wb-stage").getBoundingClientRect();
  const host = byId("wb-editor").getBoundingClientRect();
  input.style.left = `${sx + room.left - host.left}px`;
  input.style.top = `${sy + room.top - host.top}px`;
  // a turned or mirrored board shows its writing turned or mirrored, so the box you type in does too
  const lean = viewMatrix(view);
  input.style.transformOrigin = "0 0";
  input.style.transform = view.rot || view.flip ? cssOf([lean[0] / view.scale, lean[1] / view.scale, lean[2] / view.scale, lean[3] / view.scale, 0, 0]) : "";
  input.hidden = false;
  fitText();
  requestAnimationFrame(() => input.focus());
  if (hit) {
    hiddenIds.add(hit.id);
    paintLayer(target.id);
  }
}

function fitText(): void {
  const input = byId<HTMLTextAreaElement>("wb-text");
  const probe = { id: "probe", layer: "", tool: "text" as const, color: "#000", size: Number.parseFloat(input.style.font) || 24, opacity: 1, pts: [0, 0, 1], by: "", at: 0, text: input.value || " ", font: kit.font };
  const box = textBox(probe);
  input.style.width = `${Math.max(60, box.x1 - box.x0 + 12)}px`;
  input.style.height = `${Math.max(probe.size * 1.3, box.y1 - box.y0 + 4)}px`;
}

function commitText(): void {
  if (!typing || !board) return;
  const task = typing;
  typing = null;
  const input = byId<HTMLTextAreaElement>("wb-text");
  const text = input.value.replace(/\s+$/, "");
  input.hidden = true;
  input.value = "";
  const old = task.replace;
  if (old) hiddenIds.delete(old.id);
  const item = layer(task.layer);
  if (!item) return;
  if (!text.trim() || (old && text === old.text)) {
    paintLayer(task.layer);
    return;
  }
  const stroke: BoardStroke = {
    id: newId(),
    layer: task.layer,
    tool: "text",
    color: old?.color || kit.color,
    size: old?.size || kit.sizes.text / view.scale,
    opacity: old?.opacity ?? kit.alphas.text,
    ...(task.frame ? { frame: task.frame } : {}),
    pts: [task.x, task.y, 1],
    text,
    font: old?.font || kit.font,
    by: myId(),
    at: Date.now()
  };
  const ops: BoardOp[] = old ? [{ type: "erase", ids: [old.id] }, { type: "stroke", stroke }] : [{ type: "stroke", stroke }];
  const back: BoardOp[] = old ? [{ type: "erase", ids: [stroke.id] }, { type: "restore", ids: [old.id] }] : [{ type: "erase", ids: [stroke.id] }];
  const again: BoardOp[] = old ? [{ type: "erase", ids: [old.id] }, { type: "restore", ids: [stroke.id] }] : [{ type: "restore", ids: [stroke.id] }];
  run(ops);
  record({ undo: back, redo: again });
}

// --- picking things up

function selectionStrokes(): BoardStroke[] {
  if (!board || !selected.length) return [];
  const ids = new Set(selected);
  return board.strokes.filter((stroke) => ids.has(stroke.id) && !stroke.goneAt);
}

function selectionBox(): Box | null {
  let box: Box | null = null;
  for (const stroke of selectionStrokes()) box = joinBoxes(box, boxOf(stroke));
  return box;
}

// strokes this person can pick up: their own, on layers that are shown and not locked, on the frame on screen
function pickable(): BoardStroke[] {
  if (!board) return [];
  const out: BoardStroke[] = [];
  const now = performance.now();
  for (const item of liveLayers(board)) {
    if (item.hidden || item.locked) continue;
    const frame = shownFrame(item, now);
    for (const stroke of strokesOnFrame(item.id, frame)) {
      if (!isPixelTool(stroke.tool) && canEditStroke(board, stroke, actor())) out.push(stroke);
    }
  }
  return out;
}

// strokes that touch a rectangle drawn on the screen
function pickWithin(rect: Box): void {
  selected = pickable().filter((stroke) => boxesMeet(screenExtent(view, boxOf(stroke)), rect)).map((stroke) => stroke.id);
  refreshUi();
}

function pickAt(bx: number, by: number): void {
  const hit = pickable().reverse().find((stroke) => strokeHit(stroke, bx, by, 6 / view.scale));
  selected = hit ? [hit.id] : [];
  refreshUi();
}

function handleAt(x: number, y: number): Handle {
  const box = selectionBox();
  if (!box) return "";
  const near = (point: [number, number]) => Math.hypot(x - point[0], y - point[1]) <= 24;
  // the turn handle floats 34 px past the top edge, the way up looks on screen
  const middle = toScreen((box.x0 + box.x1) / 2, (box.y0 + box.y1) / 2);
  const top = toScreen((box.x0 + box.x1) / 2, box.y0);
  const lift = Math.hypot(top[0] - middle[0], top[1] - middle[1]) || 1;
  if (near([top[0] + ((top[0] - middle[0]) / lift) * 34, top[1] + ((top[1] - middle[1]) / lift) * 34])) return "rot";
  if (near(toScreen(box.x0, box.y0))) return "nw";
  if (near(toScreen(box.x1, box.y0))) return "ne";
  if (near(toScreen(box.x0, box.y1))) return "sw";
  if (near(toScreen(box.x1, box.y1))) return "se";
  const [bx, by] = toBoard(x, y);
  const slack = 6 / view.scale;
  return bx >= box.x0 - slack && bx <= box.x1 + slack && by >= box.y0 - slack && by <= box.y1 + slack ? "inside" : "";
}

// the selection frame is turned with the board, so it is placed by its middle and drawn at the view's angle
function paintSelection(): void {
  const box = selectionBox();
  const overlay = byId("wb-sel");
  if (!box || !selecting()) {
    overlay.hidden = !marquee;
    if (!marquee) return;
  }
  overlay.hidden = false;
  const picking = Boolean(marquee) && !transform;
  overlay.classList.toggle("marquee", picking);
  let base: Matrix;
  let w: number;
  let h: number;
  if (picking && marquee) {
    const x0 = Math.min(marquee.x0, marquee.x1);
    const y0 = Math.min(marquee.y0, marquee.y1);
    w = Math.abs(marquee.x1 - marquee.x0);
    h = Math.abs(marquee.y1 - marquee.y0);
    base = [1, 0, 0, 1, x0, y0];
  } else {
    const [cx, cy] = toScreen((box!.x0 + box!.x1) / 2, (box!.y0 + box!.y1) / 2);
    w = (box!.x1 - box!.x0) * view.scale;
    h = (box!.y1 - box!.y0) * view.scale;
    const cos = Math.cos(view.rot);
    const sin = Math.sin(view.rot);
    base = [cos, sin, -sin, cos, cx - (cos * w) / 2 + (sin * h) / 2, cy - (sin * w) / 2 - (cos * h) / 2];
  }
  overlay.style.left = "0";
  overlay.style.top = "0";
  overlay.style.width = `${w}px`;
  overlay.style.height = `${h}px`;
  overlay.style.transform = cssOf(transform ? multiply(onStage(view, transform.m), base) : base);
}

// a board matrix as the css matrix that does the same thing on screen
function cssMatrix(m: Matrix): string {
  return cssOf(onStage(view, m));
}

function floatSelection(): void {
  const float = byId<HTMLCanvasElement>("wb-float");
  sizeCanvas(float);
  float.hidden = false;
  float.style.transform = "";
  const ctx = float.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, float.width, float.height);
  applyView(ctx);
  const picked = selectionStrokes();
  picked.forEach((stroke) => {
    hiddenIds.add(stroke.id);
    paintStroke(ctx, stroke, 0);
  });
  new Set(picked.map((stroke) => stroke.layer)).forEach((id) => paintLayer(id));
}

function beginTransform(event: PointerEvent, handle: Handle, x: number, y: number): void {
  const box = selectionBox();
  if (!box || !handle) return;
  const [bx, by] = toBoard(x, y);
  const corners: Record<string, [number, number]> = { nw: [box.x1, box.y1], ne: [box.x0, box.y1], sw: [box.x1, box.y0], se: [box.x0, box.y0] };
  transform = {
    pointer: event.pointerId,
    handle,
    start: [bx, by],
    center: [(box.x0 + box.x1) / 2, (box.y0 + box.y1) / 2],
    anchor: corners[handle] || [(box.x0 + box.x1) / 2, (box.y0 + box.y1) / 2],
    box,
    m: [...IDENTITY],
    moved: false
  };
  floatSelection();
}

function moveTransform(x: number, y: number): void {
  if (!transform) return;
  const [bx, by] = toBoard(x, y);
  const t = transform;
  if (Math.hypot(x - toScreen(...t.start)[0], y - toScreen(...t.start)[1]) > 3) t.moved = true;
  if (t.handle === "inside") {
    t.m = [1, 0, 0, 1, bx - t.start[0], by - t.start[1]];
  } else if (t.handle === "rot") {
    const turn = Math.atan2(by - t.center[1], bx - t.center[0]) - Math.atan2(t.start[1] - t.center[1], t.start[0] - t.center[0]);
    const cos = Math.cos(turn);
    const sin = Math.sin(turn);
    t.m = [cos, sin, -sin, cos, t.center[0] - cos * t.center[0] + sin * t.center[1], t.center[1] - sin * t.center[0] - cos * t.center[1]];
  } else {
    const was = Math.hypot(t.start[0] - t.anchor[0], t.start[1] - t.anchor[1]) || 1;
    const grow = Math.min(30, Math.max(0.03, Math.hypot(bx - t.anchor[0], by - t.anchor[1]) / was));
    t.m = [grow, 0, 0, grow, t.anchor[0] * (1 - grow), t.anchor[1] * (1 - grow)];
  }
  byId("wb-float").style.transform = cssMatrix(t.m);
  paintSelection();
}

function endTransform(): void {
  if (!transform) return;
  const done = transform;
  transform = null;
  const ids = selected.slice();
  const layers = new Set(selectionStrokes().map((stroke) => stroke.layer));
  hiddenIds.clear();
  const float = byId<HTMLCanvasElement>("wb-float");
  float.hidden = true;
  const still = !done.moved || done.m.every((value, at) => Math.abs(value - IDENTITY[at]) < 1e-6);
  if (still) {
    layers.forEach((id) => paintLayer(id));
    paintSelection();
    return;
  }
  run([{ type: "xform", ids, m: done.m }]);
  record({ undo: [{ type: "xform", ids, m: invertMatrix(done.m) }], redo: [{ type: "xform", ids, m: done.m }] });
}

function duplicateSelection(): void {
  const picked = selectionStrokes();
  if (!picked.length || !board) return;
  const shift = 24 / view.scale;
  const copies = picked.map((stroke) => {
    const pts = stroke.pts.slice();
    for (let at = 0; at < pts.length; at += 3) {
      pts[at] += shift;
      pts[at + 1] += shift;
    }
    return { ...stroke, id: newId(), pts, by: myId(), at: Date.now(), goneAt: undefined };
  });
  const ids = copies.map((stroke) => stroke.id);
  run(copies.map((stroke) => ({ type: "stroke", stroke }) as BoardOp));
  record({ undo: [{ type: "erase", ids }], redo: [{ type: "restore", ids }] });
  selected = ids;
  refreshUi();
}

function deleteSelection(): void {
  const ids = selected.slice();
  if (!ids.length) return;
  selected = [];
  run([{ type: "erase", ids }]);
  record({ undo: [{ type: "restore", ids }], redo: [{ type: "erase", ids }] });
}

// --- the lasso: draw round things to pick them up, or draw an outline to fill it with the colour

function beginLasso(event: PointerEvent, x: number, y: number, fill: boolean): void {
  if (fill) {
    const target = layer();
    if (!target) return;
    if (target.hidden || target.locked) return flashToast(target.hidden ? "that layer is hidden, show it to fill" : "that layer is locked");
    if (!mayDrawOn(target)) return flashToast(`that layer is ${whose(target)}'s, ask them to let you draw on it`);
  }
  lassoing = { pointer: event.pointerId, pts: [x, y], fill };
  paintLasso();
}

function paintLasso(): void {
  const overlay = byId("wb-lasso");
  overlay.hidden = !lassoing;
  if (!lassoing) return;
  overlay.classList.toggle("fills", lassoing.fill);
  const points: string[] = [];
  for (let at = 0; at + 1 < lassoing.pts.length; at += 2) points.push(`${lassoing.pts[at]},${lassoing.pts[at + 1]}`);
  overlay.querySelector("polygon")?.setAttribute("points", points.join(" "));
}

function moveLasso(x: number, y: number): void {
  if (!lassoing) return;
  const pts = lassoing.pts;
  if (pts.length >= 7800 || Math.hypot(x - pts[pts.length - 2], y - pts[pts.length - 1]) < 3) return;
  pts.push(x, y);
  paintLasso();
}

function endLasso(cancel = false): void {
  const task = lassoing;
  lassoing = null;
  paintLasso();
  if (!task || cancel || task.pts.length < 6) return;
  const outline: number[] = [];
  for (let at = 0; at + 1 < task.pts.length; at += 2) {
    const [bx, by] = toBoard(task.pts[at], task.pts[at + 1]);
    outline.push(Math.round(bx * 10) / 10, Math.round(by * 10) / 10, 0.5);
  }
  if (task.fill) fillOutline(outline);
  else pickInside(outline);
}

// the strokes the outline holds, or only touches when it is set to
function pickInside(outline: number[]): void {
  const touch = kit.lassoTouch;
  selected = pickable().filter((stroke) => {
    const dots: Array<[number, number]> = [];
    if (stroke.tool === "text") {
      const box = boxOf(stroke);
      dots.push([box.x0, box.y0], [box.x1, box.y0], [box.x0, box.y1], [box.x1, box.y1]);
    } else {
      const count = stroke.pts.length / 3;
      const skip = Math.max(1, Math.ceil(count / 120));
      for (let at = 0; at < count; at += skip) dots.push([stroke.pts[at * 3], stroke.pts[at * 3 + 1]]);
      dots.push([stroke.pts[(count - 1) * 3], stroke.pts[(count - 1) * 3 + 1]]);
    }
    const inside = dots.map(([x, y]) => insideOutline(outline, x, y));
    return touch ? inside.some(Boolean) : inside.every(Boolean);
  }).map((stroke) => stroke.id);
  if (!selected.length) flashToast(touch ? "that outline touches nothing of yours" : "nothing of yours is inside that outline");
  refreshUi();
}

function fillOutline(outline: number[]): void {
  const target = layer();
  if (!board || !target || outline.length < 9) return;
  const frame = frameTag(target);
  const stroke: BoardStroke = {
    id: newId(),
    layer: target.id,
    tool: "fill",
    color: kit.color,
    size: 1,
    opacity: kit.alphas.fill ?? 1,
    ...(frame ? { frame } : {}),
    pts: outline.slice(0, 3999 * 3),
    by: myId(),
    at: Date.now()
  };
  run([{ type: "stroke", stroke }]);
  record({ undo: [{ type: "erase", ids: [stroke.id] }], redo: [{ type: "restore", ids: [stroke.id] }] });
  say("filled");
}

// a mirror image of what is picked up, across the middle of the pick
function flipSelection(across: "x" | "y"): void {
  const box = selectionBox();
  if (!box || !selected.length) return;
  const ids = selected.slice();
  const m: Matrix = across === "x" ? [-1, 0, 0, 1, box.x0 + box.x1, 0] : [1, 0, 0, -1, 0, box.y0 + box.y1];
  run([{ type: "xform", ids, m }]);
  record({ undo: [{ type: "xform", ids, m: invertMatrix(m) }], redo: [{ type: "xform", ids, m }] });
}

// --- the stroke eraser: whole strokes go, and only ever ones that are mine

function rubAt(x: number, y: number): void {
  if (!board || !rubbing) return;
  const [bx, by] = toBoard(x, y);
  const reach = kit.sizes.strokeEraser / 2 / view.scale;
  const now = performance.now();
  for (const item of liveLayers(board)) {
    if (item.hidden || item.locked) continue;
    for (const stroke of strokesOnFrame(item.id, shownFrame(item, now))) {
      if (rubbing.ids.has(stroke.id) || isPixelTool(stroke.tool) || !canEditStroke(board, stroke, actor())) continue;
      if (!boxesMeet(boxOf(stroke), { x0: bx - reach, y0: by - reach, x1: bx + reach, y1: by + reach })) continue;
      if (strokeHit(stroke, bx, by, reach)) {
        rubbing.ids.add(stroke.id);
        hiddenIds.add(stroke.id);
        rubbing.layers.add(item.id);
      }
    }
  }
  rubbing.layers.forEach((id) => paintLayer(id));
}

function endRub(): void {
  if (!rubbing) return;
  const ids = [...rubbing.ids];
  rubbing = null;
  hiddenIds.clear();
  if (!ids.length) return;
  run([{ type: "erase", ids }]);
  record({ undo: [{ type: "restore", ids }], redo: [{ type: "erase", ids }] });
}

// --- fingers, pencil and mouse

function fingerList(): Array<{ x: number; y: number }> {
  return [...fingers.values()].filter((finger) => finger.type === "touch");
}

function startGesture(points: Array<{ x: number; y: number }>): void {
  const mid = middle(points);
  gesture = { anchor: toBoard(mid[0], mid[1]), spread: spread(points), scale: view.scale, twist: twistOf(points), rot: view.rot, turning: false };
}

// the angle of the line between two fingers
function twistOf(points: Array<{ x: number; y: number }>): number {
  return points.length < 2 ? 0 : Math.atan2(points[1].y - points[0].y, points[1].x - points[0].x);
}

function middle(points: Array<{ x: number; y: number }>): [number, number] {
  const sum = points.reduce((acc, point) => [acc[0] + point.x, acc[1] + point.y], [0, 0]);
  return [sum[0] / points.length, sum[1] / points.length];
}

function spread(points: Array<{ x: number; y: number }>): number {
  if (points.length < 2) return 0;
  return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
}

function moveGesture(points: Array<{ x: number; y: number }>): void {
  if (!gesture || !points.length) return;
  const [mx, my] = middle(points);
  const now = spread(points);
  const scale = gesture.spread > 0 && now > 0 ? Math.min(MAX_SCALE, Math.max(MIN_SCALE, gesture.scale * (now / gesture.spread))) : view.scale;
  let rot = gesture.rot;
  if (points.length >= 2) {
    // a pinch that wobbles a few degrees stays upright, a real twist takes over from where it began
    const twist = wrap(twistOf(points) - gesture.twist);
    if (!gesture.turning && Math.abs(twist) > radians(6)) {
      gesture.turning = true;
      gesture.twist = twistOf(points);
      gesture.rot = view.rot;
    }
    if (gesture.turning) rot = wrap(gesture.rot + wrap(twistOf(points) - gesture.twist));
  }
  view = placeView({ scale, rot, flip: view.flip }, [mx, my], gesture.anchor);
  viewChanged();
}

// a turn that ends close to upright or a quarter turn settles onto it
function settleTurn(): void {
  if (!gesture?.turning) return;
  const target = snapped(view.rot);
  if (target !== view.rot) view = turnTo(view, target, stageCentre());
}

function endGesture(): void {
  settleTurn();
  gesture = null;
  window.clearTimeout(repaintTimer);
  if (!sameView(painted, view)) paintAll();
  paintViewUi();
  saveView();
}

function drawsWith(event: PointerEvent): boolean {
  if (!canDraw() || mode === "hand") return false;
  if (event.pointerType === "touch" && kit.penOnly) return false;
  if (event.pointerType === "mouse" && (event.button !== 0 || spaceDown)) return false;
  return true;
}

function onDown(event: PointerEvent): void {
  if (!board) return;
  event.preventDefault();
  if (typing) commitText();
  closePanels();
  const stage = byId("wb-stage");
  try {
    stage.setPointerCapture(event.pointerId);
  } catch {
    // some pointers can't be captured, the move events still arrive
  }
  if (event.pointerType === "pen" && !kit.penOnly) {
    kit.penOnly = true;
    keepKit();
    byId<HTMLInputElement>("wb-pen-only").checked = true;
    flashToast(t("pencil found, fingers now move the board"));
  }
  const [x, y] = stagePoint(event);
  fingers.set(event.pointerId, { x, y, sx: x, sy: y, type: event.pointerType });
  const touches = fingerList();
  if (event.pointerType === "touch" && touches.length >= 2) {
    if (live?.fromTouch) {
      // a finger stroke that only just started was the first half of a pinch
      if (performance.now() - live.startedAt < 220 || live.stroke.pts.length < 18) dropStroke();
      else endStroke();
    }
    if (transform) endTransform();
    if (rubbing) endRub();
    if (marquee) {
      marquee = null;
      paintSelection();
    }
    spinning = null;
    if (lassoing) endLasso(true);
    tap = tap && performance.now() - tap.at < 300 ? { ...tap, fingers: Math.max(tap.fingers, touches.length) } : { fingers: touches.length, at: performance.now(), moved: false };
    startGesture(touches);
    return;
  }
  if (event.pointerType === "touch") tap = { fingers: 1, at: performance.now(), moved: false };
  if (live || transform || marquee || rubbing || lassoing) return;
  ping(event, true, false, true);
  if (mode === "picker" && (event.pointerType !== "touch" || !kit.penOnly)) return pickColor(x, y);
  if (mode === "turn" && (event.pointerType !== "mouse" || (event.button === 0 && !spaceDown))) {
    const [cx, cy] = stageCentre();
    spinning = { pointer: event.pointerId, from: Math.atan2(y - cy, x - cx), rot: view.rot };
    return;
  }
  if (!drawsWith(event)) {
    // one finger, the hand tool, a middle click or space held: move the board
    startGesture([{ x, y }]);
    return;
  }
  if (selecting()) {
    const handle = handleAt(x, y);
    if (handle) return beginTransform(event, handle, x, y);
    selected = [];
    if (mode === "lasso") {
      refreshUi();
      return beginLasso(event, x, y, false);
    }
    marquee = { pointer: event.pointerId, x0: x, y0: y, x1: x, y1: y };
    refreshUi();
    return;
  }
  if (mode === "lassoFill") return beginLasso(event, x, y, true);
  if (mode === "strokeEraser") {
    rubbing = { pointer: event.pointerId, ids: new Set(), layers: new Set() };
    rubAt(x, y);
    return;
  }
  if (mode === "text") return beginText(x, y);
  beginStroke(event, x, y);
}

function onMove(event: PointerEvent): void {
  const finger = fingers.get(event.pointerId);
  const [x, y] = stagePoint(event);
  // a pen or mouse hovering is seen too, a finger only while it touches
  if (!finger && event.pointerType !== "touch") ping(event, false);
  if (!finger) return;
  if (finger) ping(event, Boolean(live || transform || marquee || rubbing));
  if (tap && Math.hypot(x - finger.sx, y - finger.sy) > 12) tap.moved = true;
  if (lassoing && lassoing.pointer === event.pointerId) {
    moveLasso(x, y);
    return;
  }
  if (spinning && spinning.pointer === event.pointerId) {
    const [cx, cy] = stageCentre();
    let rot = spinning.rot + wrap(Math.atan2(y - cy, x - cx) - spinning.from);
    // shift holds it to steps of 15 degrees
    if (event.shiftKey) rot = Math.round(rot / radians(15)) * radians(15);
    view = turnTo(view, rot, stageCentre());
    viewChanged();
    return;
  }
  if (live && live.pointer === event.pointerId) {
    extendStroke(event);
    return;
  }
  if (transform && transform.pointer === event.pointerId) {
    moveTransform(x, y);
    return;
  }
  if (marquee && marquee.pointer === event.pointerId) {
    marquee.x1 = x;
    marquee.y1 = y;
    paintSelection();
    return;
  }
  if (rubbing && rubbing.pointer === event.pointerId) {
    rubAt(x, y);
    return;
  }
  finger.x = x;
  finger.y = y;
  if (!gesture) return;
  const touches = fingerList();
  moveGesture(touches.length ? touches : [{ x, y }]);
}

function onUp(event: PointerEvent): void {
  const had = fingers.get(event.pointerId);
  fingers.delete(event.pointerId);
  if (lassoing && lassoing.pointer === event.pointerId) endLasso();
  if (spinning && spinning.pointer === event.pointerId) {
    spinning = null;
    endGesture();
  }
  if (live && live.pointer === event.pointerId) {
    extendStroke(event);
    endStroke();
  }
  if (transform && transform.pointer === event.pointerId) endTransform();
  if (rubbing && rubbing.pointer === event.pointerId) endRub();
  if (marquee && marquee.pointer === event.pointerId) {
    const done = marquee;
    marquee = null;
    const small = Math.hypot(done.x1 - done.x0, done.y1 - done.y0) < 6;
    if (small) pickAt(...toBoard(done.x1, done.y1));
    else pickWithin({ x0: Math.min(done.x0, done.x1), y0: Math.min(done.y0, done.y1), x1: Math.max(done.x0, done.x1), y1: Math.max(done.y0, done.y1) });
    paintSelection();
  }
  if (!had) return;
  if (event.pointerType !== "touch") ping(event, false, false, true);
  const touches = fingerList();
  if (gesture) {
    if (touches.length) {
      settleTurn();
      startGesture(touches);
    } else endGesture();
  }
  // a quick tap with two fingers undoes, three redoes
  if (!touches.length && tap) {
    const quick = performance.now() - tap.at < 320 && !tap.moved;
    if (quick && tap.fingers === 2) undo();
    if (quick && tap.fingers === 3) redo();
    tap = null;
  }
}

function onWheel(event: WheelEvent): void {
  if (!board) return;
  event.preventDefault();
  const [x, y] = stagePoint(event);
  // a trackpad pinch arrives as a wheel with ctrl held
  if (event.ctrlKey || event.metaKey) zoomAt(x, y, view.scale * Math.exp(-event.deltaY * 0.01));
  else if (event.altKey) {
    // alt and the wheel turn the board around the pointer
    view = turnBy(view, (event.deltaY || event.deltaX) * 0.004, [x, y]);
    viewChanged();
  } else {
    view = { ...view, x: view.x - event.deltaX, y: view.y - event.deltaY };
    viewChanged();
  }
  window.clearTimeout(wheelTimer);
  wheelTimer = window.setTimeout(endGesture, 160);
}

// --- tools, colours and faders

// the brush, text size or eraser the faders are tuning right now
function faderKey(): string {
  if (mode === "strokeEraser") return "strokeEraser";
  if (mode === "text") return "text";
  if (mode === "lassoFill") return "fill";
  if (mode === "eraser" || isRetouchMode(mode)) return mode;
  return isLineBrush(mode) ? mode : kit.tool;
}

const isRetouchMode = (value: string) => (RETOUCH_TOOLS as readonly string[]).includes(value);
const selecting = () => mode === "select" || mode === "lasso";

function setMode(next: Mode): void {
  if (typing) commitText();
  if (selecting() && next !== "select" && next !== "lasso") {
    selected = [];
    marquee = null;
  }
  if (lassoing) endLasso(true);
  mode = next;
  if (isLineBrush(next)) kit.tool = next as BrushTool;
  if (next === "eraser" || next === "strokeEraser") lastEraser = next;
  const group = TOOL_GROUPS.find((item) => item.has(next));
  if (group && group.members.length > 1) kit.picks[group.id] = next;
  keepKit();
  paintToolKeys();
  byId("wb-stage").dataset.tool = next;
  paintFaders();
  paintOptions();
  paintSelection();
  if (!byId("wb-brush-panel").hidden) showBrushes();
}

function setColor(color: string): void {
  kit.color = color;
  if (!byId("wb-brush-panel").hidden) paintBrushPanel();
  kit.recent = [color, ...kit.recent.filter((item) => item !== color)].slice(0, 10);
  keepKit();
  paintColor();
}

function paintColor(): void {
  byId("wb-color-dot").style.background = kit.color;
  byId<HTMLInputElement>("wb-color-input").value = kit.color;
  const swatch = (color: string) => {
    const key = document.createElement("button");
    key.type = "button";
    key.className = "wb-swatch";
    key.style.background = color;
    key.setAttribute("aria-label", color);
    key.classList.toggle("active", color === kit.color);
    key.addEventListener("click", () => {
      setColor(color);
      if (!isLineBrush(mode) && mode !== "shape" && mode !== "text" && mode !== "lassoFill" && !isRetouchMode(mode)) setMode(kit.tool);
      paintColor();
    });
    return key;
  };
  byId("wb-swatches").replaceChildren(...SWATCHES.map(swatch));
  byId("wb-recent").replaceChildren(...kit.recent.map(swatch));
  byId("wb-recent-head").hidden = !kit.recent.length;
}

const FADERS = {
  size: { min: 1, max: 160, log: true },
  alpha: { min: 0.03, max: 1, log: false }
};

function faderShare(kind: keyof typeof FADERS, value: number): number {
  const spec = FADERS[kind];
  return spec.log ? Math.log(value / spec.min) / Math.log(spec.max / spec.min) : (value - spec.min) / (spec.max - spec.min);
}

function faderValue(kind: keyof typeof FADERS, share: number): number {
  const spec = FADERS[kind];
  const clamped = Math.min(1, Math.max(0, share));
  return spec.log ? spec.min * Math.pow(spec.max / spec.min, clamped) : spec.min + (spec.max - spec.min) * clamped;
}

// which of the size and opacity controls the tool in hand has any use for
function faderNeeds(): { size: boolean; alpha: boolean } {
  if (["select", "lasso", "hand", "turn", "picker"].includes(mode)) return { size: false, alpha: false };
  if (mode === "strokeEraser") return { size: true, alpha: false };
  if (mode === "lassoFill") return { size: false, alpha: true };
  return { size: true, alpha: true };
}

function paintFaders(): void {
  const key = faderKey();
  const needs = faderNeeds();
  for (const kind of ["size", "alpha"] as const) {
    const value = kind === "size" ? kit.sizes[key] : kit.alphas[key];
    const fader = byId(`wb-${kind}`);
    const share = faderShare(kind, value);
    const words = kind === "size" ? `${Math.round(value)} pixels` : `${Math.round(value * 100)} percent`;
    fader.style.setProperty("--at", String(share));
    fader.dataset.value = kind === "size" ? `${Math.round(value)}` : `${Math.round(value * 100)}%`;
    fader.setAttribute("aria-valuemin", "0");
    fader.setAttribute("aria-valuemax", "100");
    fader.setAttribute("aria-valuenow", String(Math.round(share * 100)));
    fader.setAttribute("aria-valuetext", words);
    fader.hidden = !needs[kind];
    // the option bar on wide screens has a plain slider for each
    const range = byId<HTMLInputElement>(`wb-${kind}-range`);
    range.value = String(Math.round(share * 1000));
    range.setAttribute("aria-valuetext", words);
    byId(`wb-${kind}-out`).textContent = kind === "size" ? `${Math.round(value)}` : `${Math.round(value * 100)}%`;
    byId(`wb-${kind}-row`).hidden = !needs[kind];
  }
  byId("wb-faders").hidden = !needs.size && !needs.alpha;
  const dot = byId("wb-size-dot");
  dot.hidden = !needs.size;
  const size = Math.min(80, kit.sizes[key]);
  dot.style.width = `${size}px`;
  dot.style.height = `${size}px`;
  dot.style.opacity = String(Math.max(0.15, kit.alphas[key]));
  dot.style.background = key === "eraser" || key === "strokeEraser" || isRetouchMode(key) ? "transparent" : kit.color;
}

function setupFader(kind: keyof typeof FADERS): void {
  const fader = byId(`wb-${kind}`);
  const read = (event: PointerEvent) => {
    const rect = fader.getBoundingClientRect();
    const value = faderValue(kind, 1 - (event.clientY - rect.top) / rect.height);
    const key = faderKey();
    if (kind === "size") kit.sizes[key] = Math.round(value * 10) / 10;
    else kit.alphas[key] = Math.round(value * 100) / 100;
    paintFaders();
  };
  fader.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    fader.setPointerCapture(event.pointerId);
    fader.classList.add("held");
    byId("wb-editor").classList.add("sizing");
    read(event);
  });
  fader.addEventListener("pointermove", (event) => {
    if (fader.hasPointerCapture(event.pointerId)) read(event);
  });
  const done = () => {
    fader.classList.remove("held");
    byId("wb-editor").classList.remove("sizing");
    keepKit();
  };
  fader.addEventListener("pointerup", done);
  fader.addEventListener("pointercancel", done);
  // the arrow keys nudge it: small steps, shift for big ones
  fader.addEventListener("keydown", (event) => {
    const up = event.key === "ArrowUp" || event.key === "ArrowRight" || event.key === "PageUp";
    const down = event.key === "ArrowDown" || event.key === "ArrowLeft" || event.key === "PageDown";
    if (!up && !down && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    event.stopPropagation();
    const key = faderKey();
    const now = faderShare(kind, kind === "size" ? kit.sizes[key] : kit.alphas[key]);
    const step = event.shiftKey || event.key.startsWith("Page") ? 0.2 : 0.04;
    const share = event.key === "Home" ? 0 : event.key === "End" ? 1 : now + (up ? step : -step);
    const value = faderValue(kind, share);
    if (kind === "size") kit.sizes[key] = Math.round(value * 10) / 10;
    else kit.alphas[key] = Math.round(value * 100) / 100;
    paintFaders();
    keepKit();
  });
}

// --- the tool strip: seven keys, each a group of tools. press a key to pick its tool, press it again for the group

interface ToolMember {
  mode: Mode;
  label: string;
  icon: string;
  // the key on a keyboard, for the group menu
  key: string;
}

interface ToolGroup {
  id: string;
  label: string;
  members: ToolMember[];
  has: (value: string) => boolean;
}

const member = (mode: Mode, label: string, icon: string, key: string): ToolMember => ({ mode, label, icon, key });

const TOOL_GROUPS: ToolGroup[] = [
  { id: "brush", label: "brush", members: [], has: (value) => isLineBrush(value) },
  {
    id: "retouch",
    label: "retouch",
    members: [member("smudge", "smudge", "wb-smudge", "S"), member("blend", "blend", "wb-blend", "D"), member("blur", "blur", "wb-blur", "O"), member("lighten", "lighten", "wb-lighten", "Y"), member("darken", "darken", "wb-darken", "J")],
    has: (value) => isRetouchMode(value)
  },
  {
    id: "erase",
    label: "erase",
    members: [member("eraser", "rub out", "wb-eraser", "E"), member("strokeEraser", "whole strokes", "wb-stroke-erase", "X")],
    has: (value) => value === "eraser" || value === "strokeEraser"
  },
  {
    id: "select",
    label: "select",
    members: [member("select", "box select", "wb-select", "V"), member("lasso", "lasso select", "wb-lasso", "L")],
    has: (value) => value === "select" || value === "lasso"
  },
  {
    id: "shape",
    label: "shape",
    members: [member("shape", "shapes", "wb-shape", "U"), member("lassoFill", "lasso fill", "wb-fill", "C")],
    has: (value) => value === "shape" || value === "lassoFill"
  },
  { id: "text", label: "text", members: [member("text", "text", "wb-text", "T")], has: (value) => value === "text" },
  {
    id: "view",
    label: "view",
    members: [member("hand", "hand", "hand", "H"), member("turn", "turn the view", "wb-turn", "R")],
    has: (value) => value === "hand" || value === "turn"
  }
];

// what a tool key shows right now: the tool of its group you used last
function currentOf(group: ToolGroup): ToolMember {
  if (group.id === "brush") return member(kit.tool, TOOL_FEEL[kit.tool].label, TOOL_FEEL[kit.tool].icon, "B");
  return group.members.find((item) => item.mode === kit.picks[group.id]) || group.members[0];
}

// the tool in hand, for the option bar
function activeMember(): ToolMember {
  const group = TOOL_GROUPS.find((item) => item.has(mode));
  if (!group) return currentOf(TOOL_GROUPS[0]);
  if (group.id === "brush") return currentOf(group);
  return group.members.find((item) => item.mode === mode) || currentOf(group);
}

function paintToolKeys(): void {
  byId("wb-tool-keys").querySelectorAll<HTMLElement>("[data-key]").forEach((button) => {
    const group = TOOL_GROUPS.find((item) => item.id === button.dataset.key);
    if (!group) return;
    const now = currentOf(group);
    const active = group.has(mode);
    button.classList.toggle("active", active);
    button.classList.toggle("has-group", group.id === "brush" || group.members.length > 1);
    button.innerHTML = `${iconSvg(now.icon, 20)}<small>${t(group.label)}</small>`;
    button.setAttribute("aria-pressed", String(active));
    button.setAttribute("aria-label", `${t(group.label)}, ${t(now.label)}${group.members.length > 1 || group.id === "brush" ? ", press again for more" : ""}`);
    button.title = group.id === "brush" ? "brush, B P N M G A, press again for the shelf" : `${group.label}, ${group.members.map((item) => `${item.label} ${item.key}`).join(", ")}`;
  });
  paintBadge();
}

// the name of the tool in hand and its icon, at the left of the option bar
function paintBadge(): void {
  const now = activeMember();
  byId("wb-opt-icon").innerHTML = iconSvg(now.icon, 20);
  byId("wb-opt-name").textContent = t(now.label);
}

function closeFlyout(back = false): void {
  const menu = byId("wb-flyout");
  if (menu.hidden) return;
  menu.hidden = true;
  document.querySelectorAll<HTMLElement>("#wb-tool-keys [aria-expanded]").forEach((key) => key.setAttribute("aria-expanded", "false"));
  if (back) byId("wb-tool-keys").querySelector<HTMLElement>("[data-key].active")?.focus();
}

function openFlyout(group: ToolGroup, anchor: HTMLElement): void {
  const menu = byId("wb-flyout");
  menu.setAttribute("aria-label", `${group.label} tools`);
  menu.replaceChildren(...group.members.map((item) => {
    const row = document.createElement("button");
    row.type = "button";
    row.setAttribute("role", "menuitemradio");
    row.setAttribute("aria-checked", String(mode === item.mode));
    row.tabIndex = -1;
    row.innerHTML = `${iconSvg(item.icon, 20)}<span>${t(item.label)}</span><kbd>${item.key}</kbd>`;
    row.addEventListener("click", () => {
      buzz();
      closeFlyout();
      setMode(item.mode);
    });
    return row;
  }));
  const host = byId("wb-editor").getBoundingClientRect();
  const at = anchor.getBoundingClientRect();
  menu.hidden = false;
  const wide = docked();
  const height = menu.offsetHeight;
  const width = menu.offsetWidth;
  menu.style.left = `${wide ? at.right - host.left + 6 : Math.min(host.width - width - 8, Math.max(8, at.left - host.left + at.width / 2 - width / 2))}px`;
  menu.style.top = `${wide ? Math.min(host.height - height - 8, Math.max(8, at.top - host.top)) : Math.max(8, at.top - host.top - height - 8)}px`;
  anchor.setAttribute("aria-expanded", "true");
  (menu.querySelector<HTMLElement>("[aria-checked='true']") || menu.querySelector<HTMLElement>("button"))?.focus();
}

function groupKey(group: ToolGroup): HTMLElement {
  const key = document.createElement("button");
  key.type = "button";
  key.className = "wb-key wb-tool";
  key.dataset.key = group.id;
  key.setAttribute("aria-haspopup", group.id === "brush" ? "dialog" : "menu");
  key.setAttribute("aria-expanded", "false");
  let held = 0;
  let longPress = false;
  key.addEventListener("pointerdown", () => {
    longPress = false;
    window.clearTimeout(held);
    if (group.members.length > 1) {
      held = window.setTimeout(() => {
        longPress = true;
        buzz();
        openFlyout(group, key);
      }, 480);
    }
  });
  for (const stop of ["pointerup", "pointerleave", "pointercancel"]) key.addEventListener(stop, () => window.clearTimeout(held));
  key.addEventListener("click", () => {
    if (longPress) {
      longPress = false;
      return;
    }
    buzz();
    const open = !byId("wb-flyout").hidden;
    closeFlyout();
    if (group.id !== "brush") closePanels();
    if (group.id === "brush") {
      // a second press on the brush in hand opens the shelf
      if (group.has(mode)) togglePanel("wb-brush-panel");
      else setMode(kit.tool);
      return;
    }
    if (group.has(mode)) {
      if (group.members.length > 1 && !open) openFlyout(group, key);
      return;
    }
    setMode(currentOf(group).mode);
  });
  key.addEventListener("contextmenu", (event) => {
    event.preventDefault();
    if (group.members.length > 1) openFlyout(group, key);
  });
  return key;
}

function chip(label: string, press: () => void, on = false, icon = ""): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "wb-chip";
  button.classList.toggle("on", on);
  button.innerHTML = `${icon ? iconSvg(icon, 15) : ""}<span>${t(label)}</span>`;
  button.addEventListener("click", () => {
    buzz();
    press();
  });
  return button;
}

const RETOUCH_HINT: Record<string, string> = {
  smudge: "drag to smear the paint along",
  blend: "drag to mix the colour in as you smear",
  blur: "drag to soften what is under it",
  lighten: "drag to brighten what is there",
  darken: "drag to deepen what is there"
};

function hint(text: string): HTMLElement {
  const note = document.createElement("span");
  note.className = "wb-hint";
  note.textContent = text;
  return note;
}

function paintOptions(): void {
  const bar = byId("wb-options");
  const chips: HTMLElement[] = [];
  if (mode === "shape") SHAPES.forEach((kind) => chips.push(chip(kind, () => { kit.shape = kind; keepKit(); paintOptions(); }, kit.shape === kind)));
  if (mode === "text") (["sans", "serif", "mono", "display"] as const).forEach((font) => chips.push(chip(font, () => { kit.font = font; keepKit(); paintOptions(); }, kit.font === font)));
  if (mode === "eraser" || mode === "strokeEraser") {
    chips.push(chip("rub out", () => setMode("eraser"), mode === "eraser"), chip("whole strokes", () => setMode("strokeEraser"), mode === "strokeEraser"));
  }
  if (mode === "turn") {
    chips.push(hint("drag to turn the view"), chip("upright", uprightView, false, "wb-turn-left"), chip("mirror", flipView, view.flip, "wb-flip"));
  }
  if (mode === "hand") chips.push(hint("drag to move the board"));
  if (mode === "lassoFill") chips.push(hint("draw an outline to fill it with the colour"));
  if (isRetouchMode(mode)) chips.push(hint(RETOUCH_HINT[mode]));
  if (mode === "lasso") {
    chips.push(
      chip("inside", () => { kit.lassoTouch = false; keepKit(); paintOptions(); }, !kit.lassoTouch),
      chip("touching", () => { kit.lassoTouch = true; keepKit(); paintOptions(); }, kit.lassoTouch)
    );
  }
  if (selecting()) {
    if (selected.length) {
      chips.push(
        chip("duplicate", duplicateSelection, false, "wb-frame-copy"),
        chip("flip left right", () => flipSelection("x"), false, "wb-flip"),
        chip("flip up down", () => flipSelection("y"), false, "wb-flip-v"),
        chip("delete", deleteSelection, false, "trash"),
        chip("done", () => { selected = []; refreshUi(); })
      );
    } else chips.push(hint(mode === "lasso" ? "draw round what to pick" : "drag to pick, tap one stroke"));
  }
  bar.replaceChildren(...chips);
  bar.hidden = !chips.length;
  paintBadge();
}

// --- layers panel

// filled each time the panel paints, so the names follow the language
function fillBlendModes(): void {
  const option = (name: string): HTMLOptionElement => Object.assign(document.createElement("option"), { value: name, textContent: t(name.replace("-", " ")) });
  // grouped by what they do to the colours underneath, the way painting apps list them
  const groups: Array<[string, string[]]> = [
    ["darkening", ["darken", "multiply", "color-burn"]],
    ["lightening", ["lighten", "screen", "color-dodge"]],
    ["contrast", ["overlay", "soft-light", "hard-light"]],
    ["compare", ["difference", "exclusion"]]
  ];
  const known = new Set<string>(["normal", ...groups.flatMap(([, names]) => names)]);
  byId<HTMLSelectElement>("wb-layer-mode").replaceChildren(
    option("normal"),
    ...groups.map(([label, names]) => {
      const group = document.createElement("optgroup");
      group.label = t(label);
      group.append(...names.filter((name) => (BLEND_MODES as readonly string[]).includes(name)).map(option));
      return group;
    }),
    ...BLEND_MODES.filter((name) => !known.has(name)).map(option)
  );
}

function paintLayers(): void {
  if (!board) return;
  fillBlendModes();
  const list = byId("wb-layer-list");
  const rows = liveLayers(board).slice().reverse().map((item) => {
    const row = document.createElement("div");
    row.className = "wb-layer-row";
    row.classList.toggle("active", item.id === activeLayer);
    row.classList.toggle("is-hidden", item.hidden);
    const mayEdit = mayEditLayer(item);
    const key = (icon: string, label: string, action: () => void, on = false, enabled = mayEdit) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "wb-mini";
      button.classList.toggle("on", on);
      button.setAttribute("aria-label", t(label));
      button.title = t(label);
      button.innerHTML = iconSvg(icon, 16);
      button.disabled = !enabled;
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        action();
      });
      return button;
    };
    const name = document.createElement("button");
    name.type = "button";
    name.className = "wb-layer-name";
    const label = document.createElement("span");
    label.textContent = item.name;
    const timeline = layerTimeline(item);
    const small = document.createElement("small");
    const count = strokesOf(item.id).length;
    small.textContent = [timeline.length > 1 ? t("{n} frames", { n: timeline.length }) : "", board!.shared ? (item.open ? t("open") : whose(item)) : "", count ? "" : t("empty")].filter(Boolean).join(" · ");
    name.append(label, small);
    name.addEventListener("click", () => {
      activeLayer = item.id;
      refreshUi();
    });
    const owner = document.createElement("span");
    owner.className = "wb-owner-dot";
    owner.hidden = !board!.shared;
    owner.style.background = item.owner ? userColor(item.owner) : "#8a8f9c";
    owner.textContent = (people[item.owner] || (item.owner === myId() ? myName() : "?")).slice(0, 1).toUpperCase();
    row.append(
      key(item.hidden ? "eye-off" : "eye", item.hidden ? "show" : "hide", () => run([{ type: "layer-set", id: item.id, patch: { hidden: !item.hidden } }]), !item.hidden),
      owner,
      name,
      key(item.locked ? "lock" : "unlock", item.locked ? "unlock" : "lock", () => run([{ type: "layer-set", id: item.id, patch: { locked: !item.locked } }]), item.locked),
      key("up", "move up", () => moveLayer(item.id, 1)),
      key("down", "move down", () => moveLayer(item.id, -1))
    );
    return row;
  });
  list.replaceChildren(...rows);
  const current = layer();
  const mine = mayEditLayer(current);
  byId<HTMLInputElement>("wb-layer-rename").value = current?.name || "";
  byId<HTMLInputElement>("wb-layer-rename").disabled = !mine;
  byId<HTMLInputElement>("wb-layer-opacity").value = String(Math.round((current?.opacity ?? 1) * 100));
  byId<HTMLInputElement>("wb-layer-opacity").disabled = !mine;
  byId("wb-layer-opacity-value").textContent = `${Math.round((current?.opacity ?? 1) * 100)}%`;
  byId<HTMLSelectElement>("wb-layer-mode").value = current?.mode || "normal";
  byId<HTMLSelectElement>("wb-layer-mode").disabled = !mine;
  const open = byId<HTMLInputElement>("wb-layer-open");
  open.checked = Boolean(current?.open);
  open.disabled = !mine;
  byId("wb-layer-open-row").hidden = !board.shared;
  const owner = byId("wb-layer-owner");
  owner.hidden = !board.shared || !current;
  if (current) owner.textContent = mine ? t("this layer is yours") : t("{who}'s layer", { who: whose(current) }) + (current.open ? t(", open to everyone") : t(", only they draw here"));
  byId<HTMLButtonElement>("wb-layer-add").disabled = !canDraw() || liveLayers(board).length >= MAX_LAYERS;
  byId<HTMLButtonElement>("wb-layer-delete").disabled = !mine || liveLayers(board).length <= 1;
  byId<HTMLButtonElement>("wb-layer-clear").disabled = !mine;
  byId<HTMLButtonElement>("wb-layer-dup").disabled = !canDraw() || liveLayers(board).length >= MAX_LAYERS;
  byId<HTMLButtonElement>("wb-layer-animate").disabled = !mine;
  byId("wb-layer-animate").querySelector("span")!.textContent = current && current.frames.length ? t("frames") : t("animate");
  byId("wb-layer-tools").hidden = !canDraw();
  byId("wb-layers-count").textContent = String(liveLayers(board).length);
}

function moveLayer(id: string, by: number): void {
  if (!board) return;
  const layers = liveLayers(board);
  const at = layers.findIndex((item) => item.id === id);
  const neighbour = layers[at + by];
  if (!neighbour) return;
  const from = board.layers.findIndex((item) => item.id === id);
  const to = board.layers.findIndex((item) => item.id === neighbour.id);
  run([{ type: "layer-move", id, index: to }]);
  record({ undo: [{ type: "layer-move", id, index: from }], redo: [{ type: "layer-move", id, index: to }] });
}

function addLayer(): void {
  if (!board) return;
  const id = newId();
  const names = new Set(board.layers.map((item) => item.name));
  let number = liveLayers(board).length + 1;
  while (names.has(t("layer {n}", { n: number }))) number++;
  const index = board.layers.findIndex((item) => item.id === activeLayer) + 1;
  run([{ type: "layer-add", layer: newLayer(id, t("layer {n}", { n: number }), myId()), index }]);
  activeLayer = id;
  record({ undo: [{ type: "layer-remove", id }], redo: [{ type: "layer-restore", id }] });
  refreshUi();
}

// a copy of the layer, strokes and all frames, as a new layer of mine just above it
function duplicateLayer(): void {
  const item = layer();
  if (!board || !item || liveLayers(board).length >= MAX_LAYERS) return;
  const id = newId();
  const frameMap = new Map<string, string>(item.frames.map((frame) => [frame, frame ? newId() : ""]));
  const copy: BoardLayer = { ...newLayer(id, `${item.name} copy`.slice(0, 60), myId()), opacity: item.opacity, mode: item.mode, fps: item.fps, frames: item.frames.map((frame) => frameMap.get(frame) ?? frame) };
  const strokes = strokesOf(item.id).filter((stroke) => canEditStroke(board!, stroke, actor()) || !board!.shared).map((stroke) => ({
    ...stroke, id: newId(), layer: id, by: myId(), goneAt: undefined,
    ...(stroke.frame && frameMap.has(stroke.frame) ? { frame: frameMap.get(stroke.frame) } : {})
  }));
  const index = board.layers.findIndex((candidate) => candidate.id === item.id) + 1;
  const ids = strokes.map((stroke) => stroke.id);
  run([{ type: "layer-add", layer: copy, index }, ...(copy.frames.length ? [{ type: "layer-set", id, patch: { frames: copy.frames, fps: copy.fps } } as BoardOp] : []), ...strokes.map((stroke) => ({ type: "stroke", stroke }) as BoardOp)]);
  activeLayer = id;
  record({ undo: [{ type: "layer-remove", id }], redo: [{ type: "layer-restore", id }, ...(ids.length ? [{ type: "restore", ids } as BoardOp] : [])] });
  refreshUi();
}

function removeLayer(): void {
  const item = layer();
  if (!board || !item || liveLayers(board).length <= 1) return;
  if (strokesOf(item.id).length && !confirm(t("delete {name}? you can undo it", { name: item.name }))) return;
  run([{ type: "layer-remove", id: item.id }]);
  record({ undo: [{ type: "layer-restore", id: item.id }], redo: [{ type: "layer-remove", id: item.id }] });
}

function clearLayer(): void {
  const item = layer();
  if (!item) return;
  const ids = strokesOf(item.id).map((stroke) => stroke.id);
  if (!ids.length) return;
  run([{ type: "erase", ids }]);
  record({ undo: [{ type: "restore", ids }], redo: [{ type: "erase", ids }] });
  flashToast(t("{name} cleared, undo brings it back", { name: item.name }));
}

// --- the timeline: frames of the layer being drawn

function setFrames(item: BoardLayer, frames: string[], fps = item.fps): void {
  const before = { frames: item.frames.slice(), fps: item.fps };
  run([{ type: "layer-set", id: item.id, patch: { frames, fps } }]);
  record({ undo: [{ type: "layer-set", id: item.id, patch: before }], redo: [{ type: "layer-set", id: item.id, patch: { frames, fps } }] });
}

function startAnimation(): void {
  const item = layer();
  if (!item || !mayEditLayer(item)) return;
  if (item.frames.length) {
    byId("wb-timeline").hidden = false;
    closePanels();
    return;
  }
  const next = newId();
  setFrames(item, ["", next]);
  editFrames.set(item.id, next);
  closePanels();
  refreshUi();
}

function gotoFrame(item: BoardLayer, frame: string): void {
  editFrames.set(item.id, frame);
  if (previewing) setPreview(false);
  paintLayer(item.id);
  paintTimeline();
}

function stepFrame(by: number): void {
  const item = layer();
  if (!item) return;
  const timeline = layerTimeline(item);
  if (timeline.length < 2) return;
  const at = timeline.indexOf(currentFrame(item));
  gotoFrame(item, timeline[(at + by + timeline.length) % timeline.length]);
}

function addFrame(copy: boolean): void {
  const item = layer();
  if (!board || !item || !mayEditLayer(item)) return;
  const timeline = layerTimeline(item);
  if (timeline.length >= MAX_FRAMES) return flashToast(t("{n} frames is the most a layer holds", { n: MAX_FRAMES }));
  const here = currentFrame(item);
  const next = newId();
  const frames = timeline.slice();
  frames.splice(timeline.indexOf(here) + 1, 0, next);
  if (!copy) {
    setFrames(item, frames);
    editFrames.set(item.id, next);
    refreshUi();
    return;
  }
  const strokes = strokesOnFrame(item.id, here).map((stroke) => ({ ...stroke, id: newId(), frame: next, by: myId(), goneAt: undefined }));
  const ids = strokes.map((stroke) => stroke.id);
  const before = { frames: item.frames.slice(), fps: item.fps };
  run([{ type: "layer-set", id: item.id, patch: { frames } }, ...strokes.map((stroke) => ({ type: "stroke", stroke }) as BoardOp)]);
  record({ undo: [{ type: "erase", ids }, { type: "layer-set", id: item.id, patch: before }], redo: [{ type: "layer-set", id: item.id, patch: { frames } }, ...(ids.length ? [{ type: "restore", ids } as BoardOp] : [])] });
  editFrames.set(item.id, next);
  refreshUi();
}

function removeFrame(): void {
  const item = layer();
  if (!item || !mayEditLayer(item)) return;
  const timeline = layerTimeline(item);
  if (timeline.length < 2) return;
  const here = currentFrame(item);
  const at = timeline.indexOf(here);
  if (strokesOnFrame(item.id, here).length && !confirm(t("delete frame {n}? you can undo it", { n: at + 1 }))) return;
  const frames = timeline.filter((frame) => frame !== here);
  editFrames.set(item.id, frames[Math.min(at, frames.length - 1)]);
  setFrames(item, frames);
}

function shiftFrame(by: number): void {
  const item = layer();
  if (!item || !mayEditLayer(item)) return;
  const timeline = layerTimeline(item).slice();
  const at = timeline.indexOf(currentFrame(item));
  const to = at + by;
  if (to < 0 || to >= timeline.length) return;
  [timeline[at], timeline[to]] = [timeline[to], timeline[at]];
  setFrames(item, timeline);
}

function setPreview(on: boolean): void {
  previewing = on;
  const item = layer();
  if (item) paintLayer(item.id);
  paintTimeline();
  scanAnim();
}

function paintTimeline(): void {
  const bar = byId("wb-timeline");
  const item = layer();
  if (!board || !item || !item.frames.length || !canDraw()) {
    bar.hidden = true;
    return;
  }
  const timeline = layerTimeline(item);
  const here = currentFrame(item);
  const edit = mayEditLayer(item);
  const button = (icon: string, label: string, press: () => void, on = false, enabled = true) => {
    const key = document.createElement("button");
    key.type = "button";
    key.className = "wb-key wb-frame-key";
    key.classList.toggle("active", on);
    key.setAttribute("aria-label", t(label));
    key.title = t(label);
    key.disabled = !enabled;
    key.innerHTML = iconSvg(icon, 18);
    key.addEventListener("click", () => {
      buzz();
      press();
    });
    return key;
  };
  const textButton = (label: string, name: string, press: () => void, enabled: boolean) => {
    const key = document.createElement("button");
    key.type = "button";
    key.className = "wb-key wb-frame-key wb-frame-text";
    key.textContent = label;
    key.setAttribute("aria-label", t(name));
    key.title = t(name);
    key.disabled = !enabled;
    key.addEventListener("click", () => press());
    return key;
  };
  const at = timeline.indexOf(here);
  const frames = document.createElement("div");
  frames.className = "wb-frames";
  timeline.forEach((frame, number) => {
    const cell = document.createElement("button");
    cell.type = "button";
    cell.className = "wb-frame";
    cell.classList.toggle("active", frame === here && !previewing);
    cell.classList.toggle("has", strokesOnFrame(item.id, frame).length > 0);
    cell.textContent = String(number + 1);
    cell.setAttribute("aria-label", t("frame {n}", { n: number + 1 }));
    cell.addEventListener("click", () => gotoFrame(item, frame));
    frames.append(cell);
  });
  const rate = document.createElement("label");
  rate.className = "wb-rate";
  const rateInput = document.createElement("input");
  rateInput.type = "number";
  rateInput.min = "1";
  rateInput.max = "24";
  rateInput.value = String(item.fps);
  rateInput.disabled = !edit;
  rateInput.setAttribute("aria-label", t("frames per second"));
  rateInput.addEventListener("change", () => {
    const next = Math.min(24, Math.max(1, Math.round(Number(rateInput.value) || 8)));
    if (next !== item.fps) setFrames(item, item.frames.slice(), next);
  });
  rate.append(rateInput, t("fps"));
  const onion = kit.onion;
  const onionLabel = onion.on ? `${onion.before}·${onion.after}` : t("off");
  const onionKey = button("wb-onion", t("onion skin {state}", { state: onionLabel }), () => {
    // off, then one ghost each side, then two before
    if (!onion.on) Object.assign(onion, { on: true, before: 1, after: 1 });
    else if (onion.before === 1 && onion.after === 1) Object.assign(onion, { before: 2, after: 1 });
    else if (onion.before === 2) Object.assign(onion, { before: 1, after: 0 });
    else onion.on = false;
    keepKit();
    paintLayer(item.id);
    paintTimeline();
  }, onion.on);
  const tag = document.createElement("small");
  tag.className = "wb-onion-tag";
  tag.textContent = onionLabel;
  onionKey.append(tag);
  bar.replaceChildren(
    button(previewing ? "pause" : "play", previewing ? "stop playing" : "play this layer", () => setPreview(!previewing), previewing),
    button("wb-prev", "previous frame", () => stepFrame(-1)),
    frames,
    button("wb-next", "next frame", () => stepFrame(1)),
    button("wb-frame-add", "new empty frame", () => addFrame(false), false, edit && timeline.length < MAX_FRAMES),
    button("wb-frame-copy", "copy this frame", () => addFrame(true), false, edit && timeline.length < MAX_FRAMES),
    button("trash", "delete this frame", removeFrame, false, edit && timeline.length > 1),
    textButton("«", "move this frame earlier", () => shiftFrame(-1), edit && at > 0),
    textButton("»", "move this frame later", () => shiftFrame(1), edit && at < timeline.length - 1),
    onionKey,
    rate
  );
  bar.hidden = false;
  bar.querySelector<HTMLElement>(".wb-frame.active")?.scrollIntoView({ block: "nearest", inline: "nearest" });
}

// --- panels and the board sheet

// on wide screens the view, colour and layers sit in a column at the side and stay open, each folding from its title
const DOCKED = window.matchMedia("(min-width: 900px)");
const DOCK_PANELS = ["wb-brush-panel", "wb-nav-panel", "wb-color-panel", "wb-layer-panel"];
const PANEL_KEYS: Record<string, string> = { "wb-layer-panel": "wb-layers-key", "wb-nav-panel": "wb-nav-key", "wb-color-panel": "wb-color-key" };

function docked(): boolean {
  return DOCKED.matches;
}

function syncDock(): void {
  for (const id of DOCK_PANELS) byId(id).hidden = !docked();
  // tool choices ride in the option bar on wide screens and over the tool strip on narrow ones
  const choices = byId("wb-options");
  if (docked()) byId("wb-optbar").append(choices);
  else byId("wb-stack").prepend(choices);
  if (docked()) {
    paintLayers();
    showBrushes();
  }
  paintPanelKeys();
}

function paintPanelKeys(): void {
  for (const [id, key] of Object.entries(PANEL_KEYS)) {
    const panel = byId(id);
    const open = !panel.hidden && !panel.classList.contains("folded");
    byId(key).setAttribute("aria-expanded", String(open));
    byId(key).classList.toggle("active", !docked() && open);
  }
  document.querySelectorAll<HTMLButtonElement>(".wb-fold[data-fold]").forEach((button) => {
    const folded = byId(button.dataset.fold || "").classList.contains("folded");
    button.setAttribute("aria-expanded", String(!folded));
  });
}

function closePanels(except = ""): void {
  closeFlyout();
  for (const id of ["wb-layer-panel", "wb-color-panel", "wb-brush-panel", "wb-nav-panel"]) {
    if (id === except || (docked() && DOCK_PANELS.includes(id))) continue;
    byId(id).hidden = true;
  }
  paintPanelKeys();
}

function togglePanel(id: string): void {
  const panel = byId(id);
  if (docked() && DOCK_PANELS.includes(id)) {
    panel.classList.toggle("folded");
    paintPanelKeys();
    if (id === "wb-layer-panel") paintLayers();
    if (id === "wb-brush-panel") showBrushes();
    return;
  }
  const open = panel.hidden;
  closePanels(open ? id : "");
  panel.hidden = !open;
  if (open && id === "wb-layer-panel") paintLayers();
  if (open && id === "wb-brush-panel") showBrushes();
  paintPanelKeys();
}

// --- the view panel: turn, mirror and zoom, in step with whatever moved the board

const ZOOM_RANGE = Math.log(MAX_SCALE / MIN_SCALE);

function paintViewUi(): void {
  const turned = degrees(view.rot);
  const upright = turned === 0 && !view.flip;
  const zoom = `${Math.round(view.scale * 100)}%`;
  const words = `${turned === 0 ? "upright" : `${turned} degrees`}${view.flip ? ", mirrored" : ""}`;
  byId("wb-dial-needle").style.transform = `rotate(${view.rot}rad)`;
  byId("wb-dial-text").textContent = `${turned}°`;
  byId("wb-quick-angle").textContent = `${turned}°`;
  const dial = byId("wb-dial");
  dial.setAttribute("aria-valuenow", String(turned));
  dial.setAttribute("aria-valuetext", words);
  const angle = byId<HTMLInputElement>("wb-angle");
  if (document.activeElement !== angle) angle.value = String(turned);
  const zoomer = byId<HTMLInputElement>("wb-zoom");
  if (document.activeElement !== zoomer) zoomer.value = String(Math.round((Math.log(view.scale / MIN_SCALE) / ZOOM_RANGE) * 1000));
  zoomer.setAttribute("aria-valuetext", zoom);
  byId("wb-zoom-value").textContent = zoom;
  for (const id of ["wb-flip", "wb-quick-flip"]) byId(id).setAttribute("aria-pressed", String(view.flip));
  byId("wb-st-zoom").textContent = zoom;
  byId("wb-st-angle").textContent = `${turned}°`;
  byId("wb-st-view").textContent = view.flip ? "mirrored" : "upright";
  const compass = byId("wb-compass");
  compass.hidden = upright;
  byId("wb-compass-rose").style.transform = `rotate(${view.rot}rad) scaleX(${view.flip ? -1 : 1})`;
  byId("wb-compass-deg").textContent = `${turned}°`;
  compass.setAttribute("aria-label", `the view is ${words}, set it upright`);
}

function sliderScale(value: number): number {
  return MIN_SCALE * Math.exp((value / 1000) * ZOOM_RANGE);
}

function paintTitle(): void {
  if (!board) return;
  byId("wb-name").textContent = board.name;
  const read = board.shared && !canUse("whiteboard", "edit");
  byId("wb-sub").textContent = board.shared ? (read ? t("shared · look only") : t("shared · live")) : t("private");
  byId("wb-editor").classList.toggle("read-only", read);
  byId("wb-editor").classList.toggle("is-shared", board.shared);
}

function openSheet(): void {
  if (!board) return;
  const style = canStyleBoard();
  byId<HTMLInputElement>("wb-sheet-name").value = board.name;
  byId<HTMLInputElement>("wb-sheet-name").disabled = !style;
  byId("wb-sheet-look").hidden = !style;
  byId<HTMLInputElement>("wb-pen-only").checked = kit.penOnly;
  byId("wb-papers").replaceChildren(...BOARD_PAPERS.map((paper) => {
    const key = document.createElement("button");
    key.type = "button";
    key.className = "wb-swatch big";
    key.style.background = paper;
    key.setAttribute("aria-label", t("paper {color}", { color: paper }));
    key.classList.toggle("active", board?.paper === paper);
    key.addEventListener("click", () => setBoard({ paper }));
    return key;
  }));
  byId("wb-patterns").replaceChildren(...BOARD_PATTERNS.map((pattern) => {
    const key = document.createElement("button");
    key.type = "button";
    key.className = "chip";
    key.textContent = t(PATTERN_LABEL[pattern]);
    key.classList.toggle("active", board?.pattern === pattern);
    key.addEventListener("click", () => setBoard({ pattern }));
    return key;
  }));
  byId("wb-delete").hidden = board.shared && !amOwner();
  byId("wb-to-doodles").hidden = !canUse("doodles", "edit");
  byId("wb-sheet").hidden = false;
}

function setBoard(patch: { name?: string; paper?: string; pattern?: BoardPattern }): void {
  if (!board || !canStyleBoard()) return;
  const before = { name: board.name, paper: board.paper, pattern: board.pattern };
  const undoPatch = Object.fromEntries(Object.keys(patch).map((key) => [key, before[key as keyof typeof before]]));
  run([{ type: "board-set", patch }]);
  if (patch.paper || patch.pattern) record({ undo: [{ type: "board-set", patch: undoPatch }], redo: [{ type: "board-set", patch }] });
  if (!byId("wb-sheet").hidden) openSheet();
}

// the whole drawing as one picture, for the shelf, a png or the doodle wall. animated layers show their first frame
function render(maxW: number, maxH: number, scaleCap = 1, pad = 24): HTMLCanvasElement | null {
  if (!board) return null;
  const box = contentBox() || { x0: -200, y0: -150, x1: 200, y1: 150 };
  const w = box.x1 - box.x0 + pad * 2;
  const h = box.y1 - box.y0 + pad * 2;
  const scale = Math.min(scaleCap, maxW / w, maxH / h);
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(w * scale));
  out.height = Math.max(1, Math.round(h * scale));
  const ctx = out.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = board.paper;
  ctx.fillRect(0, 0, out.width, out.height);
  const sheet = document.createElement("canvas");
  sheet.width = out.width;
  sheet.height = out.height;
  const paint = sheet.getContext("2d");
  if (!paint) return null;
  for (const item of liveLayers(board)) {
    if (item.hidden) continue;
    paint.setTransform(1, 0, 0, 1, 0, 0);
    paint.clearRect(0, 0, sheet.width, sheet.height);
    paint.setTransform(scale, 0, 0, scale, (pad - box.x0) * scale, (pad - box.y0) * scale);
    strokesOnFrame(item.id, layerTimeline(item)[0]).forEach((stroke) => paintStroke(paint, stroke, 0));
    ctx.globalAlpha = item.opacity;
    ctx.globalCompositeOperation = item.mode === "normal" ? "source-over" : item.mode;
    ctx.drawImage(sheet, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
  }
  return out;
}

function fileName(): string {
  return `${(board?.name || "board").replace(/[^\w-]+/g, "-").replace(/^-+|-+$/g, "") || "board"}.png`;
}

function exportPng(): void {
  const picture = render(4096, 4096, 2);
  if (!picture) return;
  picture.toBlob((blob) => {
    if (!blob) return flashToast(t("could not make the picture"));
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName();
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(link.href), 4000);
  }, "image/png");
}

function localDay(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

async function postToDoodles(): Promise<void> {
  if (!board || !contentBox()) return flashToast(t("draw something first"));
  const picture = render(2400, 2400, 2);
  const thumb = render(480, 480, 1);
  if (!picture || !thumb) return;
  try {
    await call<Doodle[]>(CHANNELS.doodlesAdd, [{
      imageData: picture.toDataURL("image/png"),
      thumbData: thumb.toDataURL("image/jpeg", 0.8),
      title: board.name,
      author: myName(),
      date: localDay(),
      tags: ["whiteboard"]
    }]);
    flashToast(t("posted to the doodle wall"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not post it"));
  }
}

async function deleteBoard(): Promise<void> {
  if (!board || !confirm(board.shared ? t("delete {name}? this can't be undone, and it goes for everyone", { name: board.name }) : t("delete {name}? this can't be undone", { name: board.name }))) return;
  const id = board.id;
  try {
    byId("wb-sheet").hidden = true;
    outbox = outbox.filter((item) => item.board !== id);
    await call(CHANNELS.boardsRemove, id);
    closeBoard();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not delete it"));
  }
}

// --- opening and closing

export async function openBoard(id: string): Promise<void> {
  const editor = byId("wb-editor");
  try {
    const loaded = await call<Board>(CHANNELS.boardsGet, id);
    board = loaded;
    people = loaded.people || {};
    rev = loaded.rev;
    undoStack = [];
    redoStack = [];
    staleLayers = new Set();
    editFrames.clear();
    shownFrames.clear();
    hiddenIds.clear();
    selected = [];
    previewing = false;
    strokesChanged();
    lastStructure = "";
    activeLayer = defaultLayer();
    editor.hidden = false;
    byId("wb-canvases").replaceChildren();
    canvases.clear();
    syncCanvases();
    lastStructure = structureKey();
    const stage = byId("wb-stage").getBoundingClientRect();
    width = stage.width;
    height = stage.height;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    paintTitle();
    paintHistory();
    paintColor();
    setMode(canDraw() ? (mode === "hand" || mode === "turn" || mode === "picker" ? kit.tool : mode) : "hand");
    setSync("saved");
    const remembered = savedView(id);
    if (remembered) {
      view = remembered;
      paintAll();
      paintViewUi();
    } else {
      view = freshView();
      fit();
    }
    syncDock();
    refreshUi();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not open that board"));
  }
}

export function closeBoard(): void {
  if (!board) return;
  if (typing) commitText();
  if (live) endStroke();
  if (transform) endTransform();
  if (rubbing) endRub();
  ping(null, false, true);
  saveView();
  if (thumbDirty) void sendThumb();
  window.clearTimeout(thumbTimer);
  board = null;
  fingers.clear();
  gesture = null;
  spinning = null;
  lassoing = null;
  paintLasso();
  animInfo = new Map();
  hiddenIds.clear();
  clearPresence();
  clearLive();
  byId("wb-editor").hidden = true;
  byId("wb-sheet").hidden = true;
  closePanels();
  byId("wb-canvases").replaceChildren();
  canvases.clear();
  onClose();
}

export function openBoardId(): string {
  return board?.id || "";
}

// --- wiring

// the group menu: arrow keys walk it, escape and a press elsewhere close it
function setupFlyout(): void {
  const menu = byId("wb-flyout");
  menu.addEventListener("keydown", (event) => {
    const rows = [...menu.querySelectorAll<HTMLElement>("button")];
    const at = rows.indexOf(document.activeElement as HTMLElement);
    const move = (to: number) => {
      event.preventDefault();
      rows[(to + rows.length) % rows.length]?.focus();
    };
    if (event.key === "ArrowDown" || event.key === "ArrowRight") move(at + 1);
    else if (event.key === "ArrowUp" || event.key === "ArrowLeft") move(at - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(rows.length - 1);
    else if (event.key === "Escape" || event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();
      closeFlyout(true);
    }
  });
  document.addEventListener("pointerdown", (event) => {
    if (menu.hidden) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest("#wb-flyout, #wb-tool-keys")) return;
    closeFlyout();
  });
}

function openKeys(): void {
  byId("wb-keys").hidden = false;
  byId("wb-keys-close").focus();
}

function closeKeys(): void {
  byId("wb-keys").hidden = true;
  byId("wb-stage").focus();
}

// the turn dial, the angle box, the zoom slider and the buttons around them
function setupViewControls(): void {
  const dial = byId("wb-dial");
  let dragging = false;
  const aim = (event: PointerEvent) => {
    const rect = dial.getBoundingClientRect();
    const rot = Math.atan2(event.clientX - (rect.left + rect.width / 2), -(event.clientY - (rect.top + rect.height / 2)));
    view = turnTo(view, event.shiftKey ? Math.round(rot / radians(15)) * radians(15) : rot, stageCentre());
    viewChanged();
  };
  dial.addEventListener("pointerdown", (event) => {
    dragging = true;
    dial.setPointerCapture(event.pointerId);
    aim(event);
  });
  dial.addEventListener("pointermove", (event) => {
    if (dragging) aim(event);
  });
  const release = () => {
    if (!dragging) return;
    dragging = false;
    settleView();
  };
  dial.addEventListener("pointerup", release);
  dial.addEventListener("pointercancel", release);
  dial.addEventListener("keydown", (event) => {
    const step = event.shiftKey ? 15 : 1;
    const moves: Record<string, number> = { ArrowRight: step, ArrowUp: step, ArrowLeft: -step, ArrowDown: -step, PageUp: 15, PageDown: -15 };
    if (event.key in moves) turnView(radians(moves[event.key]));
    else if (event.key === "Home") turnViewTo(0);
    else return;
    event.preventDefault();
    event.stopPropagation();
  });
  const angle = byId<HTMLInputElement>("wb-angle");
  angle.addEventListener("change", () => {
    const value = Number(angle.value);
    if (Number.isFinite(value)) turnViewTo(radians(Math.max(-180, Math.min(180, value))));
  });
  const zoom = byId<HTMLInputElement>("wb-zoom");
  zoom.addEventListener("input", () => {
    view = zoomTo(view, sliderScale(Number(zoom.value)), stageCentre());
    viewChanged();
  });
  zoom.addEventListener("change", settleView);
  const press = (id: string, action: () => void) => byId(id).addEventListener("click", () => {
    buzz();
    action();
  });
  for (const id of ["wb-turn-left", "wb-quick-left"]) press(id, () => turnView(-radians(15)));
  for (const id of ["wb-turn-right", "wb-quick-right"]) press(id, () => turnView(radians(15)));
  for (const id of ["wb-flip", "wb-quick-flip"]) press(id, flipView);
  press("wb-flip-v", () => {
    view = turnBy(flipAround(view, stageCentre()), Math.PI, stageCentre());
    settleView();
    say("view upside down");
  });
  press("wb-turn-quarter", () => turnView(Math.PI / 2));
  press("wb-zoom-100", () => zoomView(1));
  press("wb-upright", uprightView);
  press("wb-nav-fit", fit);
  press("wb-compass", uprightView);
}


export function setupBoardEditor(closed: () => void): void {
  onClose = closed;
  byId("wb-tool-keys").replaceChildren(...TOOL_GROUPS.map(groupKey));
  paintToolKeys();
  setupFlyout();
  for (const kind of ["size", "alpha"] as const) {
    const range = byId<HTMLInputElement>(`wb-${kind}-range`);
    range.addEventListener("input", () => {
      const value = faderValue(kind, Number(range.value) / 1000);
      const key = faderKey();
      if (kind === "size") kit.sizes[key] = Math.round(value * 10) / 10;
      else kit.alphas[key] = Math.round(value * 100) / 100;
      paintFaders();
    });
    range.addEventListener("change", keepKit);
  }

  const stage = byId("wb-stage");
  stage.addEventListener("pointerdown", onDown);
  stage.addEventListener("pointermove", onMove);
  stage.addEventListener("pointerup", onUp);
  stage.addEventListener("pointercancel", onUp);
  stage.addEventListener("pointerleave", (event) => {
    if (event.pointerType !== "touch" && !fingers.has(event.pointerId)) ping(null, false, true);
  });
  stage.addEventListener("wheel", onWheel, { passive: false });
  // stops ios from selecting, zooming or showing the magnifier while drawing
  stage.addEventListener("touchstart", (event) => event.preventDefault(), { passive: false });
  stage.addEventListener("contextmenu", (event) => event.preventDefault());
  new ResizeObserver(() => {
    if (board) resize();
  }).observe(stage);

  setupPresence(toScreen, () => view.scale);
  setupBrushPanel({
    kit: () => kit,
    current: () => (isRetouchMode(mode) ? (mode as BrushTool) : kit.tool),
    paper: () => board?.paper || "#f6f2e8",
    pick: (tool) => {
      setMode(tool);
      keepKit();
    },
    changed: keepKit
  });

  setupFader("size");
  setupFader("alpha");

  const text = byId<HTMLTextAreaElement>("wb-text");
  text.addEventListener("input", fitText);
  text.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      text.value = "";
      commitText();
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) commitText();
    event.stopPropagation();
  });

  byId("wb-close").addEventListener("click", closeBoard);
  byId("wb-undo").addEventListener("click", undo);
  byId("wb-redo").addEventListener("click", redo);
  byId("wb-layers-key").addEventListener("click", () => togglePanel("wb-layer-panel"));
  byId("wb-color-key").addEventListener("click", () => togglePanel("wb-color-panel"));
  byId("wb-pick").addEventListener("click", () => {
    closePanels();
    setMode("picker");
  });
  byId("wb-title").addEventListener("click", openSheet);
  byId("wb-menu-key").addEventListener("click", openSheet);
  byId("wb-fit-key").addEventListener("click", fit);
  byId("wb-nav-key").addEventListener("click", () => togglePanel("wb-nav-panel"));
  byId("wb-keys-key").addEventListener("click", openKeys);
  byId("wb-keys-close").addEventListener("click", closeKeys);
  byId("wb-keys").addEventListener("click", (event) => {
    if (event.target === byId("wb-keys")) closeKeys();
  });
  document.querySelectorAll<HTMLButtonElement>(".wb-fold[data-fold]").forEach((button) => {
    button.addEventListener("click", () => togglePanel(button.dataset.fold || ""));
  });
  DOCKED.addEventListener("change", () => {
    closePanels();
    syncDock();
  });
  setupViewControls();

  byId<HTMLInputElement>("wb-color-input").addEventListener("input", (event) => {
    kit.color = (event.target as HTMLInputElement).value;
    byId("wb-color-dot").style.background = kit.color;
    paintFaders();
  });
  byId<HTMLInputElement>("wb-color-input").addEventListener("change", (event) => setColor((event.target as HTMLInputElement).value));

  byId("wb-layer-add").addEventListener("click", addLayer);
  byId("wb-layer-dup").addEventListener("click", duplicateLayer);
  byId("wb-layer-delete").addEventListener("click", removeLayer);
  byId("wb-layer-clear").addEventListener("click", clearLayer);
  byId("wb-layer-animate").addEventListener("click", startAnimation);
  const modes = byId<HTMLSelectElement>("wb-layer-mode");
  modes.addEventListener("change", () => {
    const item = layer();
    if (!item) return;
    const before = item.mode;
    const next = modes.value as BoardLayer["mode"];
    run([{ type: "layer-set", id: item.id, patch: { mode: next } }]);
    record({ undo: [{ type: "layer-set", id: item.id, patch: { mode: before } }], redo: [{ type: "layer-set", id: item.id, patch: { mode: next } }] });
  });
  byId<HTMLInputElement>("wb-layer-open").addEventListener("change", (event) => {
    const item = layer();
    if (!item) return;
    const next = (event.target as HTMLInputElement).checked;
    run([{ type: "layer-set", id: item.id, patch: { open: next } }]);
    record({ undo: [{ type: "layer-set", id: item.id, patch: { open: !next } }], redo: [{ type: "layer-set", id: item.id, patch: { open: next } }] });
  });
  byId<HTMLInputElement>("wb-layer-rename").addEventListener("change", (event) => {
    const item = layer();
    const name = (event.target as HTMLInputElement).value.trim();
    if (item && name && name !== item.name) run([{ type: "layer-set", id: item.id, patch: { name } }]);
  });
  const opacity = byId<HTMLInputElement>("wb-layer-opacity");
  let opacityBefore = 1;
  opacity.addEventListener("pointerdown", () => {
    opacityBefore = layer()?.opacity ?? 1;
  });
  opacity.addEventListener("input", () => {
    const item = layer();
    const canvas = item && canvases.get(item.id);
    if (canvas) canvas.style.opacity = String(Number(opacity.value) / 100);
    byId("wb-layer-opacity-value").textContent = `${opacity.value}%`;
  });
  opacity.addEventListener("change", () => {
    const item = layer();
    if (!item) return;
    const next = Number(opacity.value) / 100;
    run([{ type: "layer-set", id: item.id, patch: { opacity: next } }]);
    record({ undo: [{ type: "layer-set", id: item.id, patch: { opacity: opacityBefore } }], redo: [{ type: "layer-set", id: item.id, patch: { opacity: next } }] });
  });

  byId("wb-sheet-close").addEventListener("click", () => { byId("wb-sheet").hidden = true; });
  byId("wb-sheet").addEventListener("click", (event) => {
    if (event.target === byId("wb-sheet")) byId("wb-sheet").hidden = true;
  });
  byId<HTMLInputElement>("wb-sheet-name").addEventListener("change", (event) => {
    const name = (event.target as HTMLInputElement).value.trim();
    if (name && name !== board?.name) setBoard({ name });
  });
  byId<HTMLInputElement>("wb-pen-only").addEventListener("change", (event) => {
    kit.penOnly = (event.target as HTMLInputElement).checked;
    keepKit();
  });
  byId("wb-sheet-fit").addEventListener("click", () => {
    byId("wb-sheet").hidden = true;
    fit();
  });
  byId("wb-export").addEventListener("click", exportPng);
  byId("wb-to-doodles").addEventListener("click", () => void postToDoodles());
  byId("wb-delete").addEventListener("click", () => void deleteBoard());

  window.addEventListener("keydown", (event) => {
    if (!board || !byId("wb-sheet").hidden) return;
    const typingIn = (event.target as HTMLElement | null)?.closest("input, textarea, select");
    if (typingIn) return;
    const key = event.key.toLowerCase();
    if ((event.ctrlKey || event.metaKey) && key === "z") {
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && key === "y") {
      event.preventDefault();
      redo();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && key === "d") {
      event.preventDefault();
      duplicateSelection();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (!byId("wb-keys").hidden) {
      if (key === "escape") closeKeys();
      return;
    }
    if (key === "?" || (key === "/" && event.shiftKey)) {
      openKeys();
      return;
    }
    // turning, mirroring and zooming the view never touch the drawing, so anyone can
    if (key === "q" || key === "w") {
      turnView(radians(key === "w" ? 15 : -15));
      return;
    }
    if (key === "r" && event.shiftKey) {
      uprightView();
      return;
    }
    if (key === "f") {
      flipView();
      return;
    }
    if (key === "1") {
      zoomView(1);
      return;
    }
    if (key === "+" || key === "=" || key === "-" || key === "_") {
      zoomView(view.scale * (key === "+" || key === "=" ? 1.25 : 0.8));
      return;
    }
    if (key.startsWith("arrow") && !selected.length) {
      const jump = event.shiftKey ? 200 : 48;
      view = { ...view, x: view.x + (key === "arrowleft" ? jump : key === "arrowright" ? -jump : 0), y: view.y + (key === "arrowup" ? jump : key === "arrowdown" ? -jump : 0) };
      event.preventDefault();
      viewChanged();
      saveView();
      return;
    }
    // space on a button or slider is theirs, it presses or nudges
    const onControl = (event.target as HTMLElement | null)?.closest("button, [role='slider'], a, summary");
    if (key === " " && !onControl) {
      spaceDown = true;
      stage.classList.add("panning");
      event.preventDefault();
    }
    if (key === "escape") {
      if (!byId("wb-flyout").hidden) {
        closeFlyout(true);
      } else if (lassoing) {
        endLasso(true);
      } else if (mode === "turn") {
        setMode(canDraw() ? kit.tool : "hand");
      } else if (selected.length) {
        selected = [];
        refreshUi();
      } else closeBoard();
    }
    if ((key === "delete" || key === "backspace") && selected.length) {
      event.preventDefault();
      deleteSelection();
    }
    const shortcuts: Record<string, Mode> = {
      p: "pen", n: "pencil", b: "brush", m: "marker", g: "highlighter", a: "airbrush", e: "eraser", x: "strokeEraser", t: "text", v: "select", l: "lasso", c: "lassoFill", u: "shape", i: "picker",
      s: "smudge", d: "blend", o: "blur", y: "lighten", j: "darken", h: "hand", r: "turn"
    };
    if (shortcuts[key] && (canDraw() || shortcuts[key] === "hand" || shortcuts[key] === "turn")) {
      event.preventDefault();
      setMode(shortcuts[key]);
    }
    if (key === "k" && canDraw()) togglePanel("wb-brush-panel");
    if (key === "," || key === ".") stepFrame(key === "." ? 1 : -1);
    if (key === "\\") setPreview(!previewing);
    if (key === "[" || key === "]") {
      const tool = faderKey();
      kit.sizes[tool] = Math.min(160, Math.max(1, kit.sizes[tool] * (key === "]" ? 1.2 : 1 / 1.2)));
      paintFaders();
      keepKit();
    }
    if (key === "0") fit();
  });
  window.addEventListener("keyup", (event) => {
    if (event.key === " ") {
      spaceDown = false;
      stage.classList.remove("panning");
    }
  });

  listen<BoardOpsEvent>(CHANNELS.boardsOps, onOps);
  listen<BoardPresence & { shared?: boolean }>(CHANNELS.boardsPresence, onPresence);
  // back from the background, or the live line dropped: catch up on anything missed
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && board) {
      void resync();
      scanAnim();
    }
    if (document.visibilityState === "hidden" && board) {
      saveView();
      if (thumbDirty) void sendThumb();
    }
  });
}
